import * as z from 'zod';

export const MemberGetOrderRequest = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({ fieldSet: z.enum(['UNKNOWN_SET', 'BASIC', 'FULL']).optional() })
    .describe("Options for getting a logged-in member's order. ")
    .optional(),
});
export const MemberGetOrderResponse = z.object({
  order: z
    .object({
      _id: z
        .string()
        .describe('Order ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      planId: z
        .string()
        .describe(
          'ID of the plan purchased with the order, from the Plans API.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      subscriptionId: z
        .string()
        .describe(
          'ID of the related Wix subscription.\n\nEvery pricing plan order corresponds to a Wix subscription, including orders for single payment plans. Learn more in\na [Pricing Plans overview](https://support.wix.com/en/article/pricing-plans-an-overview#create-plans-to-suit-your-business).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      wixPayOrderId: z
        .string()
        .describe(
          'Wix Pay order ID.\n\nProvided by Wix whether the order is created online or offline. The field is omitted when the order is free.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      buyer: z
        .object({
          memberId: z
            .string()
            .describe('Member ID for a Wix site member, from the Members API.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          contactId: z
            .string()
            .describe(
              'Contact ID for a Wix site contact, from the Contacts API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe(
          "The buyer's IDs. Includes `memberId` and `contactId`.\n\nCurrently, Pricing Plan purchases are limited to members only. `contactId` is returned,\nbut a buyer will not be able to purchase a plan without a `memberId`."
        )
        .optional(),
      priceDetails: z
        .intersection(
          z.object({
            subtotal: z
              .string()
              .describe(
                'Price of the order excluding tax, specified as a monetary amount. for example, `"9.99"`.'
              )
              .optional(),
            discount: z.string().describe('Total discount applied.').optional(),
            tax: z
              .object({
                name: z
                  .string()
                  .describe('Name of the tax. For example, VAT.')
                  .optional(),
                includedInPrice: z
                  .boolean()
                  .describe(
                    'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                  )
                  .optional(),
                rate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                  )
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                  )
                  .optional(),
              })
              .describe('Tax applied.')
              .optional(),
            total: z
              .string()
              .describe(
                'Price after tax and discount is applied, specified as a monetary amount. For example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
              )
              .optional(),
            planPrice: z
              .string()
              .describe('Plan price as it was at the moment of order creation.')
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
              )
              .optional(),
            freeTrialDays: z
              .number()
              .int()
              .describe(
                'Free trial period for the order in days. Only available for recurring plans.'
              )
              .min(1)
              .max(999)
              .optional()
              .nullable(),
            coupon: z
              .object({
                code: z
                  .string()
                  .describe('Code of the applied coupon.')
                  .min(1)
                  .max(20)
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Total discount of the coupon, as a monetary amount.'
                  )
                  .optional(),
                _id: z
                  .string()
                  .describe('Coupon ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Coupon applied to the order. Empty means no coupon was applied.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe('Order has recurring payments.'),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'One-time payment. Order is valid for a specified duration.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'One-time payment. Order is valid until it is canceled.'
                ),
            }),
          ])
        )
        .describe(
          '__Deprecated.__ Use `pricing` instead. This property will be removed on September 30, 2022.'
        )
        .optional(),
      pricing: z
        .intersection(
          z.object({
            prices: z
              .array(
                z.object({
                  duration: z
                    .object({
                      cycleFrom: z
                        .number()
                        .int()
                        .describe(
                          'Price starts to apply with this cycle.\n\n`1` is the first payment cycle for all pricing models.'
                        )
                        .optional(),
                      numberOfCycles: z
                        .number()
                        .int()
                        .describe(
                          'Amount of cycles to apply price for.\n\nFor `subscription` pricing models with a finite number of cycles, the `numberOfCycles` is the same as `pricing.subscription.cycleCount`.\n\nFor `subscription` pricing models that are unlimited or until-canceled, the `numberOfCycles` is not returned.\n\nFor `singlePaymentForDuration` and `singlePaymentUnlimited` pricing models, the `numberOfCycles` is `1`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Cycle duration to apply `price` for.\n\nUse with all pricing models.\nCan apply the same price to multiple payment cycles.'
                    )
                    .optional(),
                  price: z
                    .object({
                      subtotal: z
                        .string()
                        .describe(
                          'Price of the order excluding tax, specified as a monetary amount. For example, `"9.99"`.'
                        )
                        .optional(),
                      coupon: z
                        .object({
                          code: z
                            .string()
                            .describe('Code of the applied coupon.')
                            .min(1)
                            .max(20)
                            .optional(),
                          amount: z
                            .string()
                            .describe(
                              'Total discount of the coupon, as a monetary amount.'
                            )
                            .optional(),
                          _id: z
                            .string()
                            .describe('Coupon ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        })
                        .describe(
                          'Coupon applied to the order, from the Coupons API.'
                        )
                        .optional(),
                      discount: z
                        .string()
                        .describe('Total discount applied to the order.')
                        .optional(),
                      tax: z
                        .object({
                          name: z
                            .string()
                            .describe('Name of the tax. For example, VAT.')
                            .optional(),
                          includedInPrice: z
                            .boolean()
                            .describe(
                              'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                            )
                            .optional(),
                          rate: z
                            .string()
                            .describe(
                              'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                            )
                            .optional(),
                          amount: z
                            .string()
                            .describe(
                              'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Tax applied to the order.\n\nTax is only applied if the site [has it configured](https://support.wix.com/en/article/pricing-plans-setting-up-tax-collection).'
                        )
                        .optional(),
                      total: z
                        .string()
                        .describe(
                          'Price after tax and discount is applied. Specified as a monetary amount, for example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Three-letter currency code in\n[ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                        )
                        .optional(),
                      proration: z
                        .string()
                        .describe(
                          'Price change after billing date was change and price was adjusted. Could be positive and negative values.'
                        )
                        .optional(),
                    })
                    .describe('Order price.')
                    .optional(),
                })
              )
              .optional(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Pricing model for an order with recurring payment cycles.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Pricing model for an order with a one-time payment and the order is valid for a specific amount of time.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'Pricing model for an order with a one-time payment and the order is valid until canceled.'
                ),
            }),
          ])
        )
        .describe(
          'Order pricing model, price, and payment schedule.\n\nLearn more about [pricing models](https://dev.wix.com/api/rest/wix-pricing-plans/pricing-plans/introduction#wix-pricing-plans_pricing-plans_introduction_pricing-models).'
        )
        .optional(),
      type: z
        .enum(['UNDEFINED', 'ONLINE', 'OFFLINE', 'EXTERNAL'])
        .describe('How the order was processed.')
        .optional(),
      status: z
        .enum([
          'UNDEFINED',
          'DRAFT',
          'PENDING',
          'ACTIVE',
          'PAUSED',
          'ENDED',
          'CANCELED',
        ])
        .describe('Status of the order.')
        .optional(),
      autoRenewCanceled: z
        .boolean()
        .describe(
          "Whether the order will be canceled at the next payment date.\n\nIf `true`, the order status will be `CANCELED` and the next payment won't be charged. Omitted for single payment orders."
        )
        .optional()
        .nullable(),
      cancellation: z
        .object({
          requestedDate: z
            .date()
            .describe('Date and time the cancellation was requested.')
            .optional()
            .nullable(),
          cause: z
            .enum([
              'UNDEFINED',
              'OWNER_ACTION',
              'MEMBER_ACTION',
              'PAYMENT_FAILURE',
              'PAYMENT_SETUP_FAILURE',
              'UNKNOWN',
            ])
            .describe('Reason for the cancellation.')
            .optional(),
          effectiveAt: z
            .enum(['UNDEFINED', 'IMMEDIATELY', 'NEXT_PAYMENT_DATE'])
            .describe(
              'When the cancellation takes effect. Set when cancelling the order.'
            )
            .optional(),
        })
        .describe(
          'Details about the cancellation of an order.\n\nOnly present if the status is `CANCELED`.'
        )
        .optional(),
      lastPaymentStatus: z
        .enum([
          'UNDEFINED',
          'PAID',
          'REFUNDED',
          'FAILED',
          'UNPAID',
          'PENDING',
          'NOT_APPLICABLE',
        ])
        .describe(
          'Status of the last payment for the order.\nUpdated automatically for online orders. Updated manually by the Wix user for offline orders.'
        )
        .optional(),
      startDate: z
        .date()
        .describe('Start date and time for the ordered plan.')
        .optional()
        .nullable(),
      endDate: z
        .date()
        .describe(
          'Current end date and time for the ordered plan.\n\n`endDate` may be updated over the course of an order.\nIf the order is paused, it will have a later `endDate` once it is resumed.\n`endDate` may also be postponed.\n\nOmitted if the order is valid until canceled and still `ACTIVE`.'
        )
        .optional()
        .nullable(),
      pausePeriods: z
        .array(
          z.object({
            status: z
              .enum(['UNDEFINED', 'ACTIVE', 'ENDED'])
              .describe('Status of the pause period.')
              .optional(),
            pauseDate: z
              .date()
              .describe('Start date and time of the pause period.')
              .optional()
              .nullable(),
            resumeDate: z
              .date()
              .describe(
                'End date and time of the pause period.\n\nOmitted while the pause period remains `ACTIVE`.'
              )
              .optional()
              .nullable(),
          })
        )
        .optional(),
      freeTrialDays: z
        .number()
        .int()
        .describe(
          'Free trial period for the order, in days.\n\nOnly available for recurring plans.'
        )
        .min(1)
        .max(999)
        .optional()
        .nullable(),
      earliestEndDate: z
        .date()
        .describe(
          'Earliest end date and time that the plan for the order can expire.\n\nCalculated by using the original end date plus any pause periods. Omitted if the order is active until canceled. Reserved for future use.'
        )
        .optional()
        .nullable(),
      currentCycle: z
        .object({
          index: z
            .number()
            .int()
            .describe(
              'Index of the current payment cycle in the order.\n\n`0` when order is in a free trial period. In all other cases, the index starts with `1`.'
            )
            .min(0)
            .optional(),
          startedDate: z
            .date()
            .describe('Start date and time for the current payment cycle.')
            .optional()
            .nullable(),
          endedDate: z
            .date()
            .describe('End date and time for the current payment cycle.')
            .optional()
            .nullable(),
        })
        .describe(
          "Current payment cycle for the order.\n\n`currentCycle` will be omitted if the order's status is `CANCELED` or `ENDED`, or if the `startDate` hasn't passed yet."
        )
        .optional(),
      planName: z
        .string()
        .describe('Plan name at the time of purchase.')
        .optional(),
      planDescription: z
        .string()
        .describe('Plan description at the time of purchase')
        .optional(),
      planPrice: z
        .string()
        .describe('Plan price as it was at the moment of order creation.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the order was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the order was updated.')
        .optional()
        .nullable(),
      formData: z
        .object({
          formId: z
            .string()
            .describe(
              'ID of the [order form](https://dev.wix.com/docs/rest/api-reference/wix-forms/form-submissions/introduction) associated with the plan at checkout.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          submissionId: z
            .string()
            .describe(
              "ID of a submission to the plan's order form at checkout. Every time a visitor completes the checkout process for a plan, a new submission is created."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          submissionData: z
            .record(z.string(), z.any())
            .describe("Data submitted to the plan's order form at checkout.")
            .optional(),
        })
        .describe(
          "Information about the form submitted during the plan's checkout."
        )
        .optional(),
    })
    .describe('Requested order.')
    .optional(),
});
export const MemberListOrdersRequest = z.object({
  options: z
    .object({
      planIds: z.array(z.string()).optional(),
      autoRenewCanceled: z
        .boolean()
        .describe('Filter for orders where auto renewal was canceled.')
        .optional()
        .nullable(),
      orderStatuses: z
        .array(
          z.enum([
            'UNDEFINED',
            'DRAFT',
            'PENDING',
            'ACTIVE',
            'PAUSED',
            'ENDED',
            'CANCELED',
          ])
        )
        .optional(),
      paymentStatuses: z
        .array(
          z.enum([
            'UNDEFINED',
            'PAID',
            'REFUNDED',
            'FAILED',
            'UNPAID',
            'PENDING',
            'NOT_APPLICABLE',
          ])
        )
        .optional(),
      limit: z
        .number()
        .int()
        .describe(
          'Limit the number of pricing plans returned. Default limit is 50.'
        )
        .min(1)
        .max(50)
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Number of entries to offset.')
        .min(0)
        .optional()
        .nullable(),
      sorting: z
        .object({
          fieldName: z
            .string()
            .describe('Name of the field to sort by.')
            .max(512)
            .optional(),
          order: z.enum(['ASC', 'DESC']).optional(),
        })
        .describe('Sorting direction (defaults to ASC) and field to sort by.')
        .optional(),
      fieldSet: z.enum(['UNKNOWN_SET', 'BASIC', 'FULL']).optional(),
    })
    .describe('Filtering, sorting, and pagination options. ')
    .optional(),
});
export const MemberListOrdersResponse = z.object({
  orders: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Order ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        planId: z
          .string()
          .describe(
            'ID of the plan purchased with the order, from the Plans API.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        subscriptionId: z
          .string()
          .describe(
            'ID of the related Wix subscription.\n\nEvery pricing plan order corresponds to a Wix subscription, including orders for single payment plans. Learn more in\na [Pricing Plans overview](https://support.wix.com/en/article/pricing-plans-an-overview#create-plans-to-suit-your-business).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        wixPayOrderId: z
          .string()
          .describe(
            'Wix Pay order ID.\n\nProvided by Wix whether the order is created online or offline. The field is omitted when the order is free.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        buyer: z
          .object({
            memberId: z
              .string()
              .describe(
                'Member ID for a Wix site member, from the Members API.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            contactId: z
              .string()
              .describe(
                'Contact ID for a Wix site contact, from the Contacts API.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe(
            "The buyer's IDs. Includes `memberId` and `contactId`.\n\nCurrently, Pricing Plan purchases are limited to members only. `contactId` is returned,\nbut a buyer will not be able to purchase a plan without a `memberId`."
          )
          .optional(),
        priceDetails: z
          .intersection(
            z.object({
              subtotal: z
                .string()
                .describe(
                  'Price of the order excluding tax, specified as a monetary amount. for example, `"9.99"`.'
                )
                .optional(),
              discount: z
                .string()
                .describe('Total discount applied.')
                .optional(),
              tax: z
                .object({
                  name: z
                    .string()
                    .describe('Name of the tax. For example, VAT.')
                    .optional(),
                  includedInPrice: z
                    .boolean()
                    .describe(
                      'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                    )
                    .optional(),
                  rate: z
                    .string()
                    .describe(
                      'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                    )
                    .optional(),
                  amount: z
                    .string()
                    .describe(
                      'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                    )
                    .optional(),
                })
                .describe('Tax applied.')
                .optional(),
              total: z
                .string()
                .describe(
                  'Price after tax and discount is applied, specified as a monetary amount. For example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
                )
                .optional(),
              planPrice: z
                .string()
                .describe(
                  'Plan price as it was at the moment of order creation.'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                )
                .optional(),
              freeTrialDays: z
                .number()
                .int()
                .describe(
                  'Free trial period for the order in days. Only available for recurring plans.'
                )
                .min(1)
                .max(999)
                .optional()
                .nullable(),
              coupon: z
                .object({
                  code: z
                    .string()
                    .describe('Code of the applied coupon.')
                    .min(1)
                    .max(20)
                    .optional(),
                  amount: z
                    .string()
                    .describe(
                      'Total discount of the coupon, as a monetary amount.'
                    )
                    .optional(),
                  _id: z
                    .string()
                    .describe('Coupon ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                })
                .describe(
                  'Coupon applied to the order. Empty means no coupon was applied.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
              }),
              z.object({
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                subscription: z
                  .object({
                    cycleDuration: z
                      .object({
                        count: z
                          .number()
                          .int()
                          .describe(
                            'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                        unit: z
                          .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                          .describe('Unit of time for the cycle duration.')
                          .optional(),
                      })
                      .describe(
                        'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                      )
                      .optional(),
                    cycleCount: z
                      .number()
                      .int()
                      .describe(
                        "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                      )
                      .min(0)
                      .max(2000)
                      .optional()
                      .nullable(),
                  })
                  .describe('Order has recurring payments.'),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                singlePaymentForDuration: z
                  .object({
                    count: z
                      .number()
                      .int()
                      .describe(
                        'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    unit: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe('Unit of time for the cycle duration.')
                      .optional(),
                  })
                  .describe(
                    'One-time payment. Order is valid for a specified duration.'
                  ),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z
                  .boolean()
                  .describe(
                    'One-time payment. Order is valid until it is canceled.'
                  ),
              }),
            ])
          )
          .describe(
            '__Deprecated.__ Use `pricing` instead. This property will be removed on September 30, 2022.'
          )
          .optional(),
        pricing: z
          .intersection(
            z.object({
              prices: z
                .array(
                  z.object({
                    duration: z
                      .object({
                        cycleFrom: z
                          .number()
                          .int()
                          .describe(
                            'Price starts to apply with this cycle.\n\n`1` is the first payment cycle for all pricing models.'
                          )
                          .optional(),
                        numberOfCycles: z
                          .number()
                          .int()
                          .describe(
                            'Amount of cycles to apply price for.\n\nFor `subscription` pricing models with a finite number of cycles, the `numberOfCycles` is the same as `pricing.subscription.cycleCount`.\n\nFor `subscription` pricing models that are unlimited or until-canceled, the `numberOfCycles` is not returned.\n\nFor `singlePaymentForDuration` and `singlePaymentUnlimited` pricing models, the `numberOfCycles` is `1`.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Cycle duration to apply `price` for.\n\nUse with all pricing models.\nCan apply the same price to multiple payment cycles.'
                      )
                      .optional(),
                    price: z
                      .object({
                        subtotal: z
                          .string()
                          .describe(
                            'Price of the order excluding tax, specified as a monetary amount. For example, `"9.99"`.'
                          )
                          .optional(),
                        coupon: z
                          .object({
                            code: z
                              .string()
                              .describe('Code of the applied coupon.')
                              .min(1)
                              .max(20)
                              .optional(),
                            amount: z
                              .string()
                              .describe(
                                'Total discount of the coupon, as a monetary amount.'
                              )
                              .optional(),
                            _id: z
                              .string()
                              .describe('Coupon ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          })
                          .describe(
                            'Coupon applied to the order, from the Coupons API.'
                          )
                          .optional(),
                        discount: z
                          .string()
                          .describe('Total discount applied to the order.')
                          .optional(),
                        tax: z
                          .object({
                            name: z
                              .string()
                              .describe('Name of the tax. For example, VAT.')
                              .optional(),
                            includedInPrice: z
                              .boolean()
                              .describe(
                                'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                              )
                              .optional(),
                            rate: z
                              .string()
                              .describe(
                                'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                              )
                              .optional(),
                            amount: z
                              .string()
                              .describe(
                                'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Tax applied to the order.\n\nTax is only applied if the site [has it configured](https://support.wix.com/en/article/pricing-plans-setting-up-tax-collection).'
                          )
                          .optional(),
                        total: z
                          .string()
                          .describe(
                            'Price after tax and discount is applied. Specified as a monetary amount, for example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Three-letter currency code in\n[ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                          )
                          .optional(),
                        proration: z
                          .string()
                          .describe(
                            'Price change after billing date was change and price was adjusted. Could be positive and negative values.'
                          )
                          .optional(),
                      })
                      .describe('Order price.')
                      .optional(),
                  })
                )
                .optional(),
            }),
            z.xor([
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
              }),
              z.object({
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                subscription: z
                  .object({
                    cycleDuration: z
                      .object({
                        count: z
                          .number()
                          .int()
                          .describe(
                            'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                        unit: z
                          .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                          .describe('Unit of time for the cycle duration.')
                          .optional(),
                      })
                      .describe(
                        'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                      )
                      .optional(),
                    cycleCount: z
                      .number()
                      .int()
                      .describe(
                        "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                      )
                      .min(0)
                      .max(2000)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Pricing model for an order with recurring payment cycles.'
                  ),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                singlePaymentForDuration: z
                  .object({
                    count: z
                      .number()
                      .int()
                      .describe(
                        'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    unit: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe('Unit of time for the cycle duration.')
                      .optional(),
                  })
                  .describe(
                    'Pricing model for an order with a one-time payment and the order is valid for a specific amount of time.'
                  ),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z
                  .boolean()
                  .describe(
                    'Pricing model for an order with a one-time payment and the order is valid until canceled.'
                  ),
              }),
            ])
          )
          .describe(
            'Order pricing model, price, and payment schedule.\n\nLearn more about [pricing models](https://dev.wix.com/api/rest/wix-pricing-plans/pricing-plans/introduction#wix-pricing-plans_pricing-plans_introduction_pricing-models).'
          )
          .optional(),
        type: z
          .enum(['UNDEFINED', 'ONLINE', 'OFFLINE', 'EXTERNAL'])
          .describe('How the order was processed.')
          .optional(),
        status: z
          .enum([
            'UNDEFINED',
            'DRAFT',
            'PENDING',
            'ACTIVE',
            'PAUSED',
            'ENDED',
            'CANCELED',
          ])
          .describe('Status of the order.')
          .optional(),
        autoRenewCanceled: z
          .boolean()
          .describe(
            "Whether the order will be canceled at the next payment date.\n\nIf `true`, the order status will be `CANCELED` and the next payment won't be charged. Omitted for single payment orders."
          )
          .optional()
          .nullable(),
        cancellation: z
          .object({
            requestedDate: z
              .date()
              .describe('Date and time the cancellation was requested.')
              .optional()
              .nullable(),
            cause: z
              .enum([
                'UNDEFINED',
                'OWNER_ACTION',
                'MEMBER_ACTION',
                'PAYMENT_FAILURE',
                'PAYMENT_SETUP_FAILURE',
                'UNKNOWN',
              ])
              .describe('Reason for the cancellation.')
              .optional(),
            effectiveAt: z
              .enum(['UNDEFINED', 'IMMEDIATELY', 'NEXT_PAYMENT_DATE'])
              .describe(
                'When the cancellation takes effect. Set when cancelling the order.'
              )
              .optional(),
          })
          .describe(
            'Details about the cancellation of an order.\n\nOnly present if the status is `CANCELED`.'
          )
          .optional(),
        lastPaymentStatus: z
          .enum([
            'UNDEFINED',
            'PAID',
            'REFUNDED',
            'FAILED',
            'UNPAID',
            'PENDING',
            'NOT_APPLICABLE',
          ])
          .describe(
            'Status of the last payment for the order.\nUpdated automatically for online orders. Updated manually by the Wix user for offline orders.'
          )
          .optional(),
        startDate: z
          .date()
          .describe('Start date and time for the ordered plan.')
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'Current end date and time for the ordered plan.\n\n`endDate` may be updated over the course of an order.\nIf the order is paused, it will have a later `endDate` once it is resumed.\n`endDate` may also be postponed.\n\nOmitted if the order is valid until canceled and still `ACTIVE`.'
          )
          .optional()
          .nullable(),
        pausePeriods: z
          .array(
            z.object({
              status: z
                .enum(['UNDEFINED', 'ACTIVE', 'ENDED'])
                .describe('Status of the pause period.')
                .optional(),
              pauseDate: z
                .date()
                .describe('Start date and time of the pause period.')
                .optional()
                .nullable(),
              resumeDate: z
                .date()
                .describe(
                  'End date and time of the pause period.\n\nOmitted while the pause period remains `ACTIVE`.'
                )
                .optional()
                .nullable(),
            })
          )
          .optional(),
        freeTrialDays: z
          .number()
          .int()
          .describe(
            'Free trial period for the order, in days.\n\nOnly available for recurring plans.'
          )
          .min(1)
          .max(999)
          .optional()
          .nullable(),
        earliestEndDate: z
          .date()
          .describe(
            'Earliest end date and time that the plan for the order can expire.\n\nCalculated by using the original end date plus any pause periods. Omitted if the order is active until canceled. Reserved for future use.'
          )
          .optional()
          .nullable(),
        currentCycle: z
          .object({
            index: z
              .number()
              .int()
              .describe(
                'Index of the current payment cycle in the order.\n\n`0` when order is in a free trial period. In all other cases, the index starts with `1`.'
              )
              .min(0)
              .optional(),
            startedDate: z
              .date()
              .describe('Start date and time for the current payment cycle.')
              .optional()
              .nullable(),
            endedDate: z
              .date()
              .describe('End date and time for the current payment cycle.')
              .optional()
              .nullable(),
          })
          .describe(
            "Current payment cycle for the order.\n\n`currentCycle` will be omitted if the order's status is `CANCELED` or `ENDED`, or if the `startDate` hasn't passed yet."
          )
          .optional(),
        planName: z
          .string()
          .describe('Plan name at the time of purchase.')
          .optional(),
        planDescription: z
          .string()
          .describe('Plan description at the time of purchase')
          .optional(),
        planPrice: z
          .string()
          .describe('Plan price as it was at the moment of order creation.')
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the order was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the order was updated.')
          .optional()
          .nullable(),
        formData: z
          .object({
            formId: z
              .string()
              .describe(
                'ID of the [order form](https://dev.wix.com/docs/rest/api-reference/wix-forms/form-submissions/introduction) associated with the plan at checkout.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            submissionId: z
              .string()
              .describe(
                "ID of a submission to the plan's order form at checkout. Every time a visitor completes the checkout process for a plan, a new submission is created."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            submissionData: z
              .record(z.string(), z.any())
              .describe("Data submitted to the plan's order form at checkout.")
              .optional(),
          })
          .describe(
            "Information about the form submitted during the plan's checkout."
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe(
      'Object containing paging-related data (number of orders returned, offset).'
    )
    .optional(),
});
export const RequestCancellationRequest = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  effectiveAt: z.enum(['UNDEFINED', 'IMMEDIATELY', 'NEXT_PAYMENT_DATE']),
});
export const RequestCancellationResponse = z.object({});
export const CreateOnlineOrderRequest = z.object({
  planId: z
    .string()
    .describe('Plan ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      startDate: z
        .date()
        .describe(
          'Start date and time for the plan of the online order in a `YYYY-MM-DDThh:mm[:ss][.sss]Z` format.\n\nDefault: Current date and time.'
        )
        .optional()
        .nullable(),
      couponCode: z
        .string()
        .describe('Coupon code to apply.')
        .min(1)
        .max(20)
        .optional()
        .nullable(),
      onBehalf: z
        .object({
          memberId: z
            .string()
            .describe('Member ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
          orderMethod: z.enum(['UNKNOWN', 'MOTO', 'POS']).optional(),
        })
        .describe("Provided if checkout is initiated on buyer's behalf.")
        .optional(),
      submissionId: z
        .string()
        .describe('Submission ID of the form submitted with this order.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CreateOnlineOrderResponse = z.object({
  order: z
    .object({
      _id: z
        .string()
        .describe('Order ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      planId: z
        .string()
        .describe(
          'ID of the plan purchased with the order, from the Plans API.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      subscriptionId: z
        .string()
        .describe(
          'ID of the related Wix subscription.\n\nEvery pricing plan order corresponds to a Wix subscription, including orders for single payment plans. Learn more in\na [Pricing Plans overview](https://support.wix.com/en/article/pricing-plans-an-overview#create-plans-to-suit-your-business).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      wixPayOrderId: z
        .string()
        .describe(
          'Wix Pay order ID.\n\nProvided by Wix whether the order is created online or offline. The field is omitted when the order is free.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      buyer: z
        .object({
          memberId: z
            .string()
            .describe('Member ID for a Wix site member, from the Members API.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          contactId: z
            .string()
            .describe(
              'Contact ID for a Wix site contact, from the Contacts API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe(
          "The buyer's IDs. Includes `memberId` and `contactId`.\n\nCurrently, Pricing Plan purchases are limited to members only. `contactId` is returned,\nbut a buyer will not be able to purchase a plan without a `memberId`."
        )
        .optional(),
      priceDetails: z
        .intersection(
          z.object({
            subtotal: z
              .string()
              .describe(
                'Price of the order excluding tax, specified as a monetary amount. for example, `"9.99"`.'
              )
              .optional(),
            discount: z.string().describe('Total discount applied.').optional(),
            tax: z
              .object({
                name: z
                  .string()
                  .describe('Name of the tax. For example, VAT.')
                  .optional(),
                includedInPrice: z
                  .boolean()
                  .describe(
                    'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                  )
                  .optional(),
                rate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                  )
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                  )
                  .optional(),
              })
              .describe('Tax applied.')
              .optional(),
            total: z
              .string()
              .describe(
                'Price after tax and discount is applied, specified as a monetary amount. For example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
              )
              .optional(),
            planPrice: z
              .string()
              .describe('Plan price as it was at the moment of order creation.')
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
              )
              .optional(),
            freeTrialDays: z
              .number()
              .int()
              .describe(
                'Free trial period for the order in days. Only available for recurring plans.'
              )
              .min(1)
              .max(999)
              .optional()
              .nullable(),
            coupon: z
              .object({
                code: z
                  .string()
                  .describe('Code of the applied coupon.')
                  .min(1)
                  .max(20)
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Total discount of the coupon, as a monetary amount.'
                  )
                  .optional(),
                _id: z
                  .string()
                  .describe('Coupon ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Coupon applied to the order. Empty means no coupon was applied.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe('Order has recurring payments.'),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'One-time payment. Order is valid for a specified duration.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'One-time payment. Order is valid until it is canceled.'
                ),
            }),
          ])
        )
        .describe(
          '__Deprecated.__ Use `pricing` instead. This property will be removed on September 30, 2022.'
        )
        .optional(),
      pricing: z
        .intersection(
          z.object({
            prices: z
              .array(
                z.object({
                  duration: z
                    .object({
                      cycleFrom: z
                        .number()
                        .int()
                        .describe(
                          'Price starts to apply with this cycle.\n\n`1` is the first payment cycle for all pricing models.'
                        )
                        .optional(),
                      numberOfCycles: z
                        .number()
                        .int()
                        .describe(
                          'Amount of cycles to apply price for.\n\nFor `subscription` pricing models with a finite number of cycles, the `numberOfCycles` is the same as `pricing.subscription.cycleCount`.\n\nFor `subscription` pricing models that are unlimited or until-canceled, the `numberOfCycles` is not returned.\n\nFor `singlePaymentForDuration` and `singlePaymentUnlimited` pricing models, the `numberOfCycles` is `1`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Cycle duration to apply `price` for.\n\nUse with all pricing models.\nCan apply the same price to multiple payment cycles.'
                    )
                    .optional(),
                  price: z
                    .object({
                      subtotal: z
                        .string()
                        .describe(
                          'Price of the order excluding tax, specified as a monetary amount. For example, `"9.99"`.'
                        )
                        .optional(),
                      coupon: z
                        .object({
                          code: z
                            .string()
                            .describe('Code of the applied coupon.')
                            .min(1)
                            .max(20)
                            .optional(),
                          amount: z
                            .string()
                            .describe(
                              'Total discount of the coupon, as a monetary amount.'
                            )
                            .optional(),
                          _id: z
                            .string()
                            .describe('Coupon ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        })
                        .describe(
                          'Coupon applied to the order, from the Coupons API.'
                        )
                        .optional(),
                      discount: z
                        .string()
                        .describe('Total discount applied to the order.')
                        .optional(),
                      tax: z
                        .object({
                          name: z
                            .string()
                            .describe('Name of the tax. For example, VAT.')
                            .optional(),
                          includedInPrice: z
                            .boolean()
                            .describe(
                              'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                            )
                            .optional(),
                          rate: z
                            .string()
                            .describe(
                              'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                            )
                            .optional(),
                          amount: z
                            .string()
                            .describe(
                              'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Tax applied to the order.\n\nTax is only applied if the site [has it configured](https://support.wix.com/en/article/pricing-plans-setting-up-tax-collection).'
                        )
                        .optional(),
                      total: z
                        .string()
                        .describe(
                          'Price after tax and discount is applied. Specified as a monetary amount, for example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Three-letter currency code in\n[ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                        )
                        .optional(),
                      proration: z
                        .string()
                        .describe(
                          'Price change after billing date was change and price was adjusted. Could be positive and negative values.'
                        )
                        .optional(),
                    })
                    .describe('Order price.')
                    .optional(),
                })
              )
              .optional(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Pricing model for an order with recurring payment cycles.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Pricing model for an order with a one-time payment and the order is valid for a specific amount of time.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'Pricing model for an order with a one-time payment and the order is valid until canceled.'
                ),
            }),
          ])
        )
        .describe(
          'Order pricing model, price, and payment schedule.\n\nLearn more about [pricing models](https://dev.wix.com/api/rest/wix-pricing-plans/pricing-plans/introduction#wix-pricing-plans_pricing-plans_introduction_pricing-models).'
        )
        .optional(),
      type: z
        .enum(['UNDEFINED', 'ONLINE', 'OFFLINE', 'EXTERNAL'])
        .describe('How the order was processed.')
        .optional(),
      status: z
        .enum([
          'UNDEFINED',
          'DRAFT',
          'PENDING',
          'ACTIVE',
          'PAUSED',
          'ENDED',
          'CANCELED',
        ])
        .describe('Status of the order.')
        .optional(),
      autoRenewCanceled: z
        .boolean()
        .describe(
          "Whether the order will be canceled at the next payment date.\n\nIf `true`, the order status will be `CANCELED` and the next payment won't be charged. Omitted for single payment orders."
        )
        .optional()
        .nullable(),
      cancellation: z
        .object({
          requestedDate: z
            .date()
            .describe('Date and time the cancellation was requested.')
            .optional()
            .nullable(),
          cause: z
            .enum([
              'UNDEFINED',
              'OWNER_ACTION',
              'MEMBER_ACTION',
              'PAYMENT_FAILURE',
              'PAYMENT_SETUP_FAILURE',
              'UNKNOWN',
            ])
            .describe('Reason for the cancellation.')
            .optional(),
          effectiveAt: z
            .enum(['UNDEFINED', 'IMMEDIATELY', 'NEXT_PAYMENT_DATE'])
            .describe(
              'When the cancellation takes effect. Set when cancelling the order.'
            )
            .optional(),
        })
        .describe(
          'Details about the cancellation of an order.\n\nOnly present if the status is `CANCELED`.'
        )
        .optional(),
      lastPaymentStatus: z
        .enum([
          'UNDEFINED',
          'PAID',
          'REFUNDED',
          'FAILED',
          'UNPAID',
          'PENDING',
          'NOT_APPLICABLE',
        ])
        .describe(
          'Status of the last payment for the order.\nUpdated automatically for online orders. Updated manually by the Wix user for offline orders.'
        )
        .optional(),
      startDate: z
        .date()
        .describe('Start date and time for the ordered plan.')
        .optional()
        .nullable(),
      endDate: z
        .date()
        .describe(
          'Current end date and time for the ordered plan.\n\n`endDate` may be updated over the course of an order.\nIf the order is paused, it will have a later `endDate` once it is resumed.\n`endDate` may also be postponed.\n\nOmitted if the order is valid until canceled and still `ACTIVE`.'
        )
        .optional()
        .nullable(),
      pausePeriods: z
        .array(
          z.object({
            status: z
              .enum(['UNDEFINED', 'ACTIVE', 'ENDED'])
              .describe('Status of the pause period.')
              .optional(),
            pauseDate: z
              .date()
              .describe('Start date and time of the pause period.')
              .optional()
              .nullable(),
            resumeDate: z
              .date()
              .describe(
                'End date and time of the pause period.\n\nOmitted while the pause period remains `ACTIVE`.'
              )
              .optional()
              .nullable(),
          })
        )
        .optional(),
      freeTrialDays: z
        .number()
        .int()
        .describe(
          'Free trial period for the order, in days.\n\nOnly available for recurring plans.'
        )
        .min(1)
        .max(999)
        .optional()
        .nullable(),
      earliestEndDate: z
        .date()
        .describe(
          'Earliest end date and time that the plan for the order can expire.\n\nCalculated by using the original end date plus any pause periods. Omitted if the order is active until canceled. Reserved for future use.'
        )
        .optional()
        .nullable(),
      currentCycle: z
        .object({
          index: z
            .number()
            .int()
            .describe(
              'Index of the current payment cycle in the order.\n\n`0` when order is in a free trial period. In all other cases, the index starts with `1`.'
            )
            .min(0)
            .optional(),
          startedDate: z
            .date()
            .describe('Start date and time for the current payment cycle.')
            .optional()
            .nullable(),
          endedDate: z
            .date()
            .describe('End date and time for the current payment cycle.')
            .optional()
            .nullable(),
        })
        .describe(
          "Current payment cycle for the order.\n\n`currentCycle` will be omitted if the order's status is `CANCELED` or `ENDED`, or if the `startDate` hasn't passed yet."
        )
        .optional(),
      planName: z
        .string()
        .describe('Plan name at the time of purchase.')
        .optional(),
      planDescription: z
        .string()
        .describe('Plan description at the time of purchase')
        .optional(),
      planPrice: z
        .string()
        .describe('Plan price as it was at the moment of order creation.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the order was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the order was updated.')
        .optional()
        .nullable(),
      formData: z
        .object({
          formId: z
            .string()
            .describe(
              'ID of the [order form](https://dev.wix.com/docs/rest/api-reference/wix-forms/form-submissions/introduction) associated with the plan at checkout.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          submissionId: z
            .string()
            .describe(
              "ID of a submission to the plan's order form at checkout. Every time a visitor completes the checkout process for a plan, a new submission is created."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          submissionData: z
            .record(z.string(), z.any())
            .describe("Data submitted to the plan's order form at checkout.")
            .optional(),
        })
        .describe(
          "Information about the form submitted during the plan's checkout."
        )
        .optional(),
    })
    .describe('Created online order.')
    .optional(),
});
export const CreateOfflineOrderRequest = z.object({
  planId: z
    .string()
    .describe('ID of the plan being ordered, from the Plans API.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  memberId: z
    .string()
    .describe('ID of the member ordering the plan, from the Members API.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      startDate: z
        .date()
        .describe(
          'Start date and time for the ordered plan in a `YYYY-MM-DDThh:mm[:ss][.sss]Z` format.\n\nDefault: Current date and time.'
        )
        .optional()
        .nullable(),
      paid: z
        .boolean()
        .describe('Whether the order is paid.\n\nDefault: `false`')
        .optional()
        .nullable(),
      couponCode: z
        .string()
        .describe('Coupon code to apply, from the Coupons API.')
        .min(1)
        .max(20)
        .optional()
        .nullable(),
      submissionId: z
        .string()
        .describe('Form submission ID that was submitted with the order.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('Options for creating an offline order. ')
    .optional(),
});
export const CreateOfflineOrderResponse = z.object({
  order: z
    .object({
      _id: z
        .string()
        .describe('Order ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      planId: z
        .string()
        .describe(
          'ID of the plan purchased with the order, from the Plans API.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      subscriptionId: z
        .string()
        .describe(
          'ID of the related Wix subscription.\n\nEvery pricing plan order corresponds to a Wix subscription, including orders for single payment plans. Learn more in\na [Pricing Plans overview](https://support.wix.com/en/article/pricing-plans-an-overview#create-plans-to-suit-your-business).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      wixPayOrderId: z
        .string()
        .describe(
          'Wix Pay order ID.\n\nProvided by Wix whether the order is created online or offline. The field is omitted when the order is free.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      buyer: z
        .object({
          memberId: z
            .string()
            .describe('Member ID for a Wix site member, from the Members API.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          contactId: z
            .string()
            .describe(
              'Contact ID for a Wix site contact, from the Contacts API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe(
          "The buyer's IDs. Includes `memberId` and `contactId`.\n\nCurrently, Pricing Plan purchases are limited to members only. `contactId` is returned,\nbut a buyer will not be able to purchase a plan without a `memberId`."
        )
        .optional(),
      priceDetails: z
        .intersection(
          z.object({
            subtotal: z
              .string()
              .describe(
                'Price of the order excluding tax, specified as a monetary amount. for example, `"9.99"`.'
              )
              .optional(),
            discount: z.string().describe('Total discount applied.').optional(),
            tax: z
              .object({
                name: z
                  .string()
                  .describe('Name of the tax. For example, VAT.')
                  .optional(),
                includedInPrice: z
                  .boolean()
                  .describe(
                    'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                  )
                  .optional(),
                rate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                  )
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                  )
                  .optional(),
              })
              .describe('Tax applied.')
              .optional(),
            total: z
              .string()
              .describe(
                'Price after tax and discount is applied, specified as a monetary amount. For example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
              )
              .optional(),
            planPrice: z
              .string()
              .describe('Plan price as it was at the moment of order creation.')
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
              )
              .optional(),
            freeTrialDays: z
              .number()
              .int()
              .describe(
                'Free trial period for the order in days. Only available for recurring plans.'
              )
              .min(1)
              .max(999)
              .optional()
              .nullable(),
            coupon: z
              .object({
                code: z
                  .string()
                  .describe('Code of the applied coupon.')
                  .min(1)
                  .max(20)
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Total discount of the coupon, as a monetary amount.'
                  )
                  .optional(),
                _id: z
                  .string()
                  .describe('Coupon ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Coupon applied to the order. Empty means no coupon was applied.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe('Order has recurring payments.'),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'One-time payment. Order is valid for a specified duration.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'One-time payment. Order is valid until it is canceled.'
                ),
            }),
          ])
        )
        .describe(
          '__Deprecated.__ Use `pricing` instead. This property will be removed on September 30, 2022.'
        )
        .optional(),
      pricing: z
        .intersection(
          z.object({
            prices: z
              .array(
                z.object({
                  duration: z
                    .object({
                      cycleFrom: z
                        .number()
                        .int()
                        .describe(
                          'Price starts to apply with this cycle.\n\n`1` is the first payment cycle for all pricing models.'
                        )
                        .optional(),
                      numberOfCycles: z
                        .number()
                        .int()
                        .describe(
                          'Amount of cycles to apply price for.\n\nFor `subscription` pricing models with a finite number of cycles, the `numberOfCycles` is the same as `pricing.subscription.cycleCount`.\n\nFor `subscription` pricing models that are unlimited or until-canceled, the `numberOfCycles` is not returned.\n\nFor `singlePaymentForDuration` and `singlePaymentUnlimited` pricing models, the `numberOfCycles` is `1`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Cycle duration to apply `price` for.\n\nUse with all pricing models.\nCan apply the same price to multiple payment cycles.'
                    )
                    .optional(),
                  price: z
                    .object({
                      subtotal: z
                        .string()
                        .describe(
                          'Price of the order excluding tax, specified as a monetary amount. For example, `"9.99"`.'
                        )
                        .optional(),
                      coupon: z
                        .object({
                          code: z
                            .string()
                            .describe('Code of the applied coupon.')
                            .min(1)
                            .max(20)
                            .optional(),
                          amount: z
                            .string()
                            .describe(
                              'Total discount of the coupon, as a monetary amount.'
                            )
                            .optional(),
                          _id: z
                            .string()
                            .describe('Coupon ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        })
                        .describe(
                          'Coupon applied to the order, from the Coupons API.'
                        )
                        .optional(),
                      discount: z
                        .string()
                        .describe('Total discount applied to the order.')
                        .optional(),
                      tax: z
                        .object({
                          name: z
                            .string()
                            .describe('Name of the tax. For example, VAT.')
                            .optional(),
                          includedInPrice: z
                            .boolean()
                            .describe(
                              'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                            )
                            .optional(),
                          rate: z
                            .string()
                            .describe(
                              'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                            )
                            .optional(),
                          amount: z
                            .string()
                            .describe(
                              'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Tax applied to the order.\n\nTax is only applied if the site [has it configured](https://support.wix.com/en/article/pricing-plans-setting-up-tax-collection).'
                        )
                        .optional(),
                      total: z
                        .string()
                        .describe(
                          'Price after tax and discount is applied. Specified as a monetary amount, for example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Three-letter currency code in\n[ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                        )
                        .optional(),
                      proration: z
                        .string()
                        .describe(
                          'Price change after billing date was change and price was adjusted. Could be positive and negative values.'
                        )
                        .optional(),
                    })
                    .describe('Order price.')
                    .optional(),
                })
              )
              .optional(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Pricing model for an order with recurring payment cycles.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Pricing model for an order with a one-time payment and the order is valid for a specific amount of time.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'Pricing model for an order with a one-time payment and the order is valid until canceled.'
                ),
            }),
          ])
        )
        .describe(
          'Order pricing model, price, and payment schedule.\n\nLearn more about [pricing models](https://dev.wix.com/api/rest/wix-pricing-plans/pricing-plans/introduction#wix-pricing-plans_pricing-plans_introduction_pricing-models).'
        )
        .optional(),
      type: z
        .enum(['UNDEFINED', 'ONLINE', 'OFFLINE', 'EXTERNAL'])
        .describe('How the order was processed.')
        .optional(),
      status: z
        .enum([
          'UNDEFINED',
          'DRAFT',
          'PENDING',
          'ACTIVE',
          'PAUSED',
          'ENDED',
          'CANCELED',
        ])
        .describe('Status of the order.')
        .optional(),
      autoRenewCanceled: z
        .boolean()
        .describe(
          "Whether the order will be canceled at the next payment date.\n\nIf `true`, the order status will be `CANCELED` and the next payment won't be charged. Omitted for single payment orders."
        )
        .optional()
        .nullable(),
      cancellation: z
        .object({
          requestedDate: z
            .date()
            .describe('Date and time the cancellation was requested.')
            .optional()
            .nullable(),
          cause: z
            .enum([
              'UNDEFINED',
              'OWNER_ACTION',
              'MEMBER_ACTION',
              'PAYMENT_FAILURE',
              'PAYMENT_SETUP_FAILURE',
              'UNKNOWN',
            ])
            .describe('Reason for the cancellation.')
            .optional(),
          effectiveAt: z
            .enum(['UNDEFINED', 'IMMEDIATELY', 'NEXT_PAYMENT_DATE'])
            .describe(
              'When the cancellation takes effect. Set when cancelling the order.'
            )
            .optional(),
        })
        .describe(
          'Details about the cancellation of an order.\n\nOnly present if the status is `CANCELED`.'
        )
        .optional(),
      lastPaymentStatus: z
        .enum([
          'UNDEFINED',
          'PAID',
          'REFUNDED',
          'FAILED',
          'UNPAID',
          'PENDING',
          'NOT_APPLICABLE',
        ])
        .describe(
          'Status of the last payment for the order.\nUpdated automatically for online orders. Updated manually by the Wix user for offline orders.'
        )
        .optional(),
      startDate: z
        .date()
        .describe('Start date and time for the ordered plan.')
        .optional()
        .nullable(),
      endDate: z
        .date()
        .describe(
          'Current end date and time for the ordered plan.\n\n`endDate` may be updated over the course of an order.\nIf the order is paused, it will have a later `endDate` once it is resumed.\n`endDate` may also be postponed.\n\nOmitted if the order is valid until canceled and still `ACTIVE`.'
        )
        .optional()
        .nullable(),
      pausePeriods: z
        .array(
          z.object({
            status: z
              .enum(['UNDEFINED', 'ACTIVE', 'ENDED'])
              .describe('Status of the pause period.')
              .optional(),
            pauseDate: z
              .date()
              .describe('Start date and time of the pause period.')
              .optional()
              .nullable(),
            resumeDate: z
              .date()
              .describe(
                'End date and time of the pause period.\n\nOmitted while the pause period remains `ACTIVE`.'
              )
              .optional()
              .nullable(),
          })
        )
        .optional(),
      freeTrialDays: z
        .number()
        .int()
        .describe(
          'Free trial period for the order, in days.\n\nOnly available for recurring plans.'
        )
        .min(1)
        .max(999)
        .optional()
        .nullable(),
      earliestEndDate: z
        .date()
        .describe(
          'Earliest end date and time that the plan for the order can expire.\n\nCalculated by using the original end date plus any pause periods. Omitted if the order is active until canceled. Reserved for future use.'
        )
        .optional()
        .nullable(),
      currentCycle: z
        .object({
          index: z
            .number()
            .int()
            .describe(
              'Index of the current payment cycle in the order.\n\n`0` when order is in a free trial period. In all other cases, the index starts with `1`.'
            )
            .min(0)
            .optional(),
          startedDate: z
            .date()
            .describe('Start date and time for the current payment cycle.')
            .optional()
            .nullable(),
          endedDate: z
            .date()
            .describe('End date and time for the current payment cycle.')
            .optional()
            .nullable(),
        })
        .describe(
          "Current payment cycle for the order.\n\n`currentCycle` will be omitted if the order's status is `CANCELED` or `ENDED`, or if the `startDate` hasn't passed yet."
        )
        .optional(),
      planName: z
        .string()
        .describe('Plan name at the time of purchase.')
        .optional(),
      planDescription: z
        .string()
        .describe('Plan description at the time of purchase')
        .optional(),
      planPrice: z
        .string()
        .describe('Plan price as it was at the moment of order creation.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the order was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the order was updated.')
        .optional()
        .nullable(),
      formData: z
        .object({
          formId: z
            .string()
            .describe(
              'ID of the [order form](https://dev.wix.com/docs/rest/api-reference/wix-forms/form-submissions/introduction) associated with the plan at checkout.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          submissionId: z
            .string()
            .describe(
              "ID of a submission to the plan's order form at checkout. Every time a visitor completes the checkout process for a plan, a new submission is created."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          submissionData: z
            .record(z.string(), z.any())
            .describe("Data submitted to the plan's order form at checkout.")
            .optional(),
        })
        .describe(
          "Information about the form submitted during the plan's checkout."
        )
        .optional(),
    })
    .describe('Order.')
    .optional(),
});
export const GetOnlineOrderPreviewRequest = z.object({
  planId: z
    .string()
    .describe('Plan ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      startDate: z
        .date()
        .describe(
          'Start date and time for the plan of the order preview in a `YYYY-MM-DDThh:mm[:ss][.sss]Z` format.\n\nDefault: Current date and time.'
        )
        .optional()
        .nullable(),
      couponCode: z
        .string()
        .describe('Coupon code to apply.')
        .min(1)
        .max(20)
        .optional()
        .nullable(),
    })
    .optional(),
});
export const GetOnlineOrderPreviewResponse = z.object({
  order: z
    .object({
      _id: z
        .string()
        .describe('Order ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      planId: z
        .string()
        .describe(
          'ID of the plan purchased with the order, from the Plans API.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      subscriptionId: z
        .string()
        .describe(
          'ID of the related Wix subscription.\n\nEvery pricing plan order corresponds to a Wix subscription, including orders for single payment plans. Learn more in\na [Pricing Plans overview](https://support.wix.com/en/article/pricing-plans-an-overview#create-plans-to-suit-your-business).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      wixPayOrderId: z
        .string()
        .describe(
          'Wix Pay order ID.\n\nProvided by Wix whether the order is created online or offline. The field is omitted when the order is free.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      buyer: z
        .object({
          memberId: z
            .string()
            .describe('Member ID for a Wix site member, from the Members API.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          contactId: z
            .string()
            .describe(
              'Contact ID for a Wix site contact, from the Contacts API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe(
          "The buyer's IDs. Includes `memberId` and `contactId`.\n\nCurrently, Pricing Plan purchases are limited to members only. `contactId` is returned,\nbut a buyer will not be able to purchase a plan without a `memberId`."
        )
        .optional(),
      priceDetails: z
        .intersection(
          z.object({
            subtotal: z
              .string()
              .describe(
                'Price of the order excluding tax, specified as a monetary amount. for example, `"9.99"`.'
              )
              .optional(),
            discount: z.string().describe('Total discount applied.').optional(),
            tax: z
              .object({
                name: z
                  .string()
                  .describe('Name of the tax. For example, VAT.')
                  .optional(),
                includedInPrice: z
                  .boolean()
                  .describe(
                    'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                  )
                  .optional(),
                rate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                  )
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                  )
                  .optional(),
              })
              .describe('Tax applied.')
              .optional(),
            total: z
              .string()
              .describe(
                'Price after tax and discount is applied, specified as a monetary amount. For example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
              )
              .optional(),
            planPrice: z
              .string()
              .describe('Plan price as it was at the moment of order creation.')
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
              )
              .optional(),
            freeTrialDays: z
              .number()
              .int()
              .describe(
                'Free trial period for the order in days. Only available for recurring plans.'
              )
              .min(1)
              .max(999)
              .optional()
              .nullable(),
            coupon: z
              .object({
                code: z
                  .string()
                  .describe('Code of the applied coupon.')
                  .min(1)
                  .max(20)
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Total discount of the coupon, as a monetary amount.'
                  )
                  .optional(),
                _id: z
                  .string()
                  .describe('Coupon ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Coupon applied to the order. Empty means no coupon was applied.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe('Order has recurring payments.'),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'One-time payment. Order is valid for a specified duration.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'One-time payment. Order is valid until it is canceled.'
                ),
            }),
          ])
        )
        .describe(
          '__Deprecated.__ Use `pricing` instead. This property will be removed on September 30, 2022.'
        )
        .optional(),
      pricing: z
        .intersection(
          z.object({
            prices: z
              .array(
                z.object({
                  duration: z
                    .object({
                      cycleFrom: z
                        .number()
                        .int()
                        .describe(
                          'Price starts to apply with this cycle.\n\n`1` is the first payment cycle for all pricing models.'
                        )
                        .optional(),
                      numberOfCycles: z
                        .number()
                        .int()
                        .describe(
                          'Amount of cycles to apply price for.\n\nFor `subscription` pricing models with a finite number of cycles, the `numberOfCycles` is the same as `pricing.subscription.cycleCount`.\n\nFor `subscription` pricing models that are unlimited or until-canceled, the `numberOfCycles` is not returned.\n\nFor `singlePaymentForDuration` and `singlePaymentUnlimited` pricing models, the `numberOfCycles` is `1`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Cycle duration to apply `price` for.\n\nUse with all pricing models.\nCan apply the same price to multiple payment cycles.'
                    )
                    .optional(),
                  price: z
                    .object({
                      subtotal: z
                        .string()
                        .describe(
                          'Price of the order excluding tax, specified as a monetary amount. For example, `"9.99"`.'
                        )
                        .optional(),
                      coupon: z
                        .object({
                          code: z
                            .string()
                            .describe('Code of the applied coupon.')
                            .min(1)
                            .max(20)
                            .optional(),
                          amount: z
                            .string()
                            .describe(
                              'Total discount of the coupon, as a monetary amount.'
                            )
                            .optional(),
                          _id: z
                            .string()
                            .describe('Coupon ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        })
                        .describe(
                          'Coupon applied to the order, from the Coupons API.'
                        )
                        .optional(),
                      discount: z
                        .string()
                        .describe('Total discount applied to the order.')
                        .optional(),
                      tax: z
                        .object({
                          name: z
                            .string()
                            .describe('Name of the tax. For example, VAT.')
                            .optional(),
                          includedInPrice: z
                            .boolean()
                            .describe(
                              'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                            )
                            .optional(),
                          rate: z
                            .string()
                            .describe(
                              'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                            )
                            .optional(),
                          amount: z
                            .string()
                            .describe(
                              'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Tax applied to the order.\n\nTax is only applied if the site [has it configured](https://support.wix.com/en/article/pricing-plans-setting-up-tax-collection).'
                        )
                        .optional(),
                      total: z
                        .string()
                        .describe(
                          'Price after tax and discount is applied. Specified as a monetary amount, for example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Three-letter currency code in\n[ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                        )
                        .optional(),
                      proration: z
                        .string()
                        .describe(
                          'Price change after billing date was change and price was adjusted. Could be positive and negative values.'
                        )
                        .optional(),
                    })
                    .describe('Order price.')
                    .optional(),
                })
              )
              .optional(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Pricing model for an order with recurring payment cycles.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Pricing model for an order with a one-time payment and the order is valid for a specific amount of time.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'Pricing model for an order with a one-time payment and the order is valid until canceled.'
                ),
            }),
          ])
        )
        .describe(
          'Order pricing model, price, and payment schedule.\n\nLearn more about [pricing models](https://dev.wix.com/api/rest/wix-pricing-plans/pricing-plans/introduction#wix-pricing-plans_pricing-plans_introduction_pricing-models).'
        )
        .optional(),
      type: z
        .enum(['UNDEFINED', 'ONLINE', 'OFFLINE', 'EXTERNAL'])
        .describe('How the order was processed.')
        .optional(),
      status: z
        .enum([
          'UNDEFINED',
          'DRAFT',
          'PENDING',
          'ACTIVE',
          'PAUSED',
          'ENDED',
          'CANCELED',
        ])
        .describe('Status of the order.')
        .optional(),
      autoRenewCanceled: z
        .boolean()
        .describe(
          "Whether the order will be canceled at the next payment date.\n\nIf `true`, the order status will be `CANCELED` and the next payment won't be charged. Omitted for single payment orders."
        )
        .optional()
        .nullable(),
      cancellation: z
        .object({
          requestedDate: z
            .date()
            .describe('Date and time the cancellation was requested.')
            .optional()
            .nullable(),
          cause: z
            .enum([
              'UNDEFINED',
              'OWNER_ACTION',
              'MEMBER_ACTION',
              'PAYMENT_FAILURE',
              'PAYMENT_SETUP_FAILURE',
              'UNKNOWN',
            ])
            .describe('Reason for the cancellation.')
            .optional(),
          effectiveAt: z
            .enum(['UNDEFINED', 'IMMEDIATELY', 'NEXT_PAYMENT_DATE'])
            .describe(
              'When the cancellation takes effect. Set when cancelling the order.'
            )
            .optional(),
        })
        .describe(
          'Details about the cancellation of an order.\n\nOnly present if the status is `CANCELED`.'
        )
        .optional(),
      lastPaymentStatus: z
        .enum([
          'UNDEFINED',
          'PAID',
          'REFUNDED',
          'FAILED',
          'UNPAID',
          'PENDING',
          'NOT_APPLICABLE',
        ])
        .describe(
          'Status of the last payment for the order.\nUpdated automatically for online orders. Updated manually by the Wix user for offline orders.'
        )
        .optional(),
      startDate: z
        .date()
        .describe('Start date and time for the ordered plan.')
        .optional()
        .nullable(),
      endDate: z
        .date()
        .describe(
          'Current end date and time for the ordered plan.\n\n`endDate` may be updated over the course of an order.\nIf the order is paused, it will have a later `endDate` once it is resumed.\n`endDate` may also be postponed.\n\nOmitted if the order is valid until canceled and still `ACTIVE`.'
        )
        .optional()
        .nullable(),
      pausePeriods: z
        .array(
          z.object({
            status: z
              .enum(['UNDEFINED', 'ACTIVE', 'ENDED'])
              .describe('Status of the pause period.')
              .optional(),
            pauseDate: z
              .date()
              .describe('Start date and time of the pause period.')
              .optional()
              .nullable(),
            resumeDate: z
              .date()
              .describe(
                'End date and time of the pause period.\n\nOmitted while the pause period remains `ACTIVE`.'
              )
              .optional()
              .nullable(),
          })
        )
        .optional(),
      freeTrialDays: z
        .number()
        .int()
        .describe(
          'Free trial period for the order, in days.\n\nOnly available for recurring plans.'
        )
        .min(1)
        .max(999)
        .optional()
        .nullable(),
      earliestEndDate: z
        .date()
        .describe(
          'Earliest end date and time that the plan for the order can expire.\n\nCalculated by using the original end date plus any pause periods. Omitted if the order is active until canceled. Reserved for future use.'
        )
        .optional()
        .nullable(),
      currentCycle: z
        .object({
          index: z
            .number()
            .int()
            .describe(
              'Index of the current payment cycle in the order.\n\n`0` when order is in a free trial period. In all other cases, the index starts with `1`.'
            )
            .min(0)
            .optional(),
          startedDate: z
            .date()
            .describe('Start date and time for the current payment cycle.')
            .optional()
            .nullable(),
          endedDate: z
            .date()
            .describe('End date and time for the current payment cycle.')
            .optional()
            .nullable(),
        })
        .describe(
          "Current payment cycle for the order.\n\n`currentCycle` will be omitted if the order's status is `CANCELED` or `ENDED`, or if the `startDate` hasn't passed yet."
        )
        .optional(),
      planName: z
        .string()
        .describe('Plan name at the time of purchase.')
        .optional(),
      planDescription: z
        .string()
        .describe('Plan description at the time of purchase')
        .optional(),
      planPrice: z
        .string()
        .describe('Plan price as it was at the moment of order creation.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the order was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the order was updated.')
        .optional()
        .nullable(),
      formData: z
        .object({
          formId: z
            .string()
            .describe(
              'ID of the [order form](https://dev.wix.com/docs/rest/api-reference/wix-forms/form-submissions/introduction) associated with the plan at checkout.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          submissionId: z
            .string()
            .describe(
              "ID of a submission to the plan's order form at checkout. Every time a visitor completes the checkout process for a plan, a new submission is created."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          submissionData: z
            .record(z.string(), z.any())
            .describe("Data submitted to the plan's order form at checkout.")
            .optional(),
        })
        .describe(
          "Information about the form submitted during the plan's checkout."
        )
        .optional(),
    })
    .describe(
      "Order preview. This field is undefined if the member has already reached the purchase limit for the order's plan."
    )
    .optional(),
  purchaseLimitExceeded: z
    .boolean()
    .describe(
      "Whether the member has already reached purchase limit for the order's plan."
    )
    .optional(),
});
export const GetOfflineOrderPreviewRequest = z.object({
  planId: z
    .string()
    .describe('ID of the plan of the previewed order.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  memberId: z
    .string()
    .describe(
      'Member ID of the buyer the previewed order is for, from the Members API.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      startDate: z
        .date()
        .describe(
          'Start date and time for plan of the previewed order in a `YYYY-MM-DDThh:mm[:ss][.sss]Z` format.\n\nDefault: Current date and time.'
        )
        .optional()
        .nullable(),
      couponCode: z
        .string()
        .describe('Coupon code to apply, from the Coupons API.')
        .min(1)
        .max(20)
        .optional()
        .nullable(),
    })
    .describe('Options for previewing the offline order. ')
    .optional(),
});
export const GetOfflineOrderPreviewResponse = z.object({
  order: z
    .object({
      _id: z
        .string()
        .describe('Order ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      planId: z
        .string()
        .describe(
          'ID of the plan purchased with the order, from the Plans API.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      subscriptionId: z
        .string()
        .describe(
          'ID of the related Wix subscription.\n\nEvery pricing plan order corresponds to a Wix subscription, including orders for single payment plans. Learn more in\na [Pricing Plans overview](https://support.wix.com/en/article/pricing-plans-an-overview#create-plans-to-suit-your-business).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      wixPayOrderId: z
        .string()
        .describe(
          'Wix Pay order ID.\n\nProvided by Wix whether the order is created online or offline. The field is omitted when the order is free.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      buyer: z
        .object({
          memberId: z
            .string()
            .describe('Member ID for a Wix site member, from the Members API.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          contactId: z
            .string()
            .describe(
              'Contact ID for a Wix site contact, from the Contacts API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe(
          "The buyer's IDs. Includes `memberId` and `contactId`.\n\nCurrently, Pricing Plan purchases are limited to members only. `contactId` is returned,\nbut a buyer will not be able to purchase a plan without a `memberId`."
        )
        .optional(),
      priceDetails: z
        .intersection(
          z.object({
            subtotal: z
              .string()
              .describe(
                'Price of the order excluding tax, specified as a monetary amount. for example, `"9.99"`.'
              )
              .optional(),
            discount: z.string().describe('Total discount applied.').optional(),
            tax: z
              .object({
                name: z
                  .string()
                  .describe('Name of the tax. For example, VAT.')
                  .optional(),
                includedInPrice: z
                  .boolean()
                  .describe(
                    'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                  )
                  .optional(),
                rate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                  )
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                  )
                  .optional(),
              })
              .describe('Tax applied.')
              .optional(),
            total: z
              .string()
              .describe(
                'Price after tax and discount is applied, specified as a monetary amount. For example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
              )
              .optional(),
            planPrice: z
              .string()
              .describe('Plan price as it was at the moment of order creation.')
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
              )
              .optional(),
            freeTrialDays: z
              .number()
              .int()
              .describe(
                'Free trial period for the order in days. Only available for recurring plans.'
              )
              .min(1)
              .max(999)
              .optional()
              .nullable(),
            coupon: z
              .object({
                code: z
                  .string()
                  .describe('Code of the applied coupon.')
                  .min(1)
                  .max(20)
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Total discount of the coupon, as a monetary amount.'
                  )
                  .optional(),
                _id: z
                  .string()
                  .describe('Coupon ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Coupon applied to the order. Empty means no coupon was applied.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe('Order has recurring payments.'),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'One-time payment. Order is valid for a specified duration.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'One-time payment. Order is valid until it is canceled.'
                ),
            }),
          ])
        )
        .describe(
          '__Deprecated.__ Use `pricing` instead. This property will be removed on September 30, 2022.'
        )
        .optional(),
      pricing: z
        .intersection(
          z.object({
            prices: z
              .array(
                z.object({
                  duration: z
                    .object({
                      cycleFrom: z
                        .number()
                        .int()
                        .describe(
                          'Price starts to apply with this cycle.\n\n`1` is the first payment cycle for all pricing models.'
                        )
                        .optional(),
                      numberOfCycles: z
                        .number()
                        .int()
                        .describe(
                          'Amount of cycles to apply price for.\n\nFor `subscription` pricing models with a finite number of cycles, the `numberOfCycles` is the same as `pricing.subscription.cycleCount`.\n\nFor `subscription` pricing models that are unlimited or until-canceled, the `numberOfCycles` is not returned.\n\nFor `singlePaymentForDuration` and `singlePaymentUnlimited` pricing models, the `numberOfCycles` is `1`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Cycle duration to apply `price` for.\n\nUse with all pricing models.\nCan apply the same price to multiple payment cycles.'
                    )
                    .optional(),
                  price: z
                    .object({
                      subtotal: z
                        .string()
                        .describe(
                          'Price of the order excluding tax, specified as a monetary amount. For example, `"9.99"`.'
                        )
                        .optional(),
                      coupon: z
                        .object({
                          code: z
                            .string()
                            .describe('Code of the applied coupon.')
                            .min(1)
                            .max(20)
                            .optional(),
                          amount: z
                            .string()
                            .describe(
                              'Total discount of the coupon, as a monetary amount.'
                            )
                            .optional(),
                          _id: z
                            .string()
                            .describe('Coupon ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        })
                        .describe(
                          'Coupon applied to the order, from the Coupons API.'
                        )
                        .optional(),
                      discount: z
                        .string()
                        .describe('Total discount applied to the order.')
                        .optional(),
                      tax: z
                        .object({
                          name: z
                            .string()
                            .describe('Name of the tax. For example, VAT.')
                            .optional(),
                          includedInPrice: z
                            .boolean()
                            .describe(
                              'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                            )
                            .optional(),
                          rate: z
                            .string()
                            .describe(
                              'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                            )
                            .optional(),
                          amount: z
                            .string()
                            .describe(
                              'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Tax applied to the order.\n\nTax is only applied if the site [has it configured](https://support.wix.com/en/article/pricing-plans-setting-up-tax-collection).'
                        )
                        .optional(),
                      total: z
                        .string()
                        .describe(
                          'Price after tax and discount is applied. Specified as a monetary amount, for example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
                        )
                        .optional(),
                      currency: z
                        .string()
                        .describe(
                          'Three-letter currency code in\n[ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                        )
                        .optional(),
                      proration: z
                        .string()
                        .describe(
                          'Price change after billing date was change and price was adjusted. Could be positive and negative values.'
                        )
                        .optional(),
                    })
                    .describe('Order price.')
                    .optional(),
                })
              )
              .optional(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Pricing model for an order with recurring payment cycles.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Pricing model for an order with a one-time payment and the order is valid for a specific amount of time.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'Pricing model for an order with a one-time payment and the order is valid until canceled.'
                ),
            }),
          ])
        )
        .describe(
          'Order pricing model, price, and payment schedule.\n\nLearn more about [pricing models](https://dev.wix.com/api/rest/wix-pricing-plans/pricing-plans/introduction#wix-pricing-plans_pricing-plans_introduction_pricing-models).'
        )
        .optional(),
      type: z
        .enum(['UNDEFINED', 'ONLINE', 'OFFLINE', 'EXTERNAL'])
        .describe('How the order was processed.')
        .optional(),
      status: z
        .enum([
          'UNDEFINED',
          'DRAFT',
          'PENDING',
          'ACTIVE',
          'PAUSED',
          'ENDED',
          'CANCELED',
        ])
        .describe('Status of the order.')
        .optional(),
      autoRenewCanceled: z
        .boolean()
        .describe(
          "Whether the order will be canceled at the next payment date.\n\nIf `true`, the order status will be `CANCELED` and the next payment won't be charged. Omitted for single payment orders."
        )
        .optional()
        .nullable(),
      cancellation: z
        .object({
          requestedDate: z
            .date()
            .describe('Date and time the cancellation was requested.')
            .optional()
            .nullable(),
          cause: z
            .enum([
              'UNDEFINED',
              'OWNER_ACTION',
              'MEMBER_ACTION',
              'PAYMENT_FAILURE',
              'PAYMENT_SETUP_FAILURE',
              'UNKNOWN',
            ])
            .describe('Reason for the cancellation.')
            .optional(),
          effectiveAt: z
            .enum(['UNDEFINED', 'IMMEDIATELY', 'NEXT_PAYMENT_DATE'])
            .describe(
              'When the cancellation takes effect. Set when cancelling the order.'
            )
            .optional(),
        })
        .describe(
          'Details about the cancellation of an order.\n\nOnly present if the status is `CANCELED`.'
        )
        .optional(),
      lastPaymentStatus: z
        .enum([
          'UNDEFINED',
          'PAID',
          'REFUNDED',
          'FAILED',
          'UNPAID',
          'PENDING',
          'NOT_APPLICABLE',
        ])
        .describe(
          'Status of the last payment for the order.\nUpdated automatically for online orders. Updated manually by the Wix user for offline orders.'
        )
        .optional(),
      startDate: z
        .date()
        .describe('Start date and time for the ordered plan.')
        .optional()
        .nullable(),
      endDate: z
        .date()
        .describe(
          'Current end date and time for the ordered plan.\n\n`endDate` may be updated over the course of an order.\nIf the order is paused, it will have a later `endDate` once it is resumed.\n`endDate` may also be postponed.\n\nOmitted if the order is valid until canceled and still `ACTIVE`.'
        )
        .optional()
        .nullable(),
      pausePeriods: z
        .array(
          z.object({
            status: z
              .enum(['UNDEFINED', 'ACTIVE', 'ENDED'])
              .describe('Status of the pause period.')
              .optional(),
            pauseDate: z
              .date()
              .describe('Start date and time of the pause period.')
              .optional()
              .nullable(),
            resumeDate: z
              .date()
              .describe(
                'End date and time of the pause period.\n\nOmitted while the pause period remains `ACTIVE`.'
              )
              .optional()
              .nullable(),
          })
        )
        .optional(),
      freeTrialDays: z
        .number()
        .int()
        .describe(
          'Free trial period for the order, in days.\n\nOnly available for recurring plans.'
        )
        .min(1)
        .max(999)
        .optional()
        .nullable(),
      earliestEndDate: z
        .date()
        .describe(
          'Earliest end date and time that the plan for the order can expire.\n\nCalculated by using the original end date plus any pause periods. Omitted if the order is active until canceled. Reserved for future use.'
        )
        .optional()
        .nullable(),
      currentCycle: z
        .object({
          index: z
            .number()
            .int()
            .describe(
              'Index of the current payment cycle in the order.\n\n`0` when order is in a free trial period. In all other cases, the index starts with `1`.'
            )
            .min(0)
            .optional(),
          startedDate: z
            .date()
            .describe('Start date and time for the current payment cycle.')
            .optional()
            .nullable(),
          endedDate: z
            .date()
            .describe('End date and time for the current payment cycle.')
            .optional()
            .nullable(),
        })
        .describe(
          "Current payment cycle for the order.\n\n`currentCycle` will be omitted if the order's status is `CANCELED` or `ENDED`, or if the `startDate` hasn't passed yet."
        )
        .optional(),
      planName: z
        .string()
        .describe('Plan name at the time of purchase.')
        .optional(),
      planDescription: z
        .string()
        .describe('Plan description at the time of purchase')
        .optional(),
      planPrice: z
        .string()
        .describe('Plan price as it was at the moment of order creation.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the order was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the order was updated.')
        .optional()
        .nullable(),
      formData: z
        .object({
          formId: z
            .string()
            .describe(
              'ID of the [order form](https://dev.wix.com/docs/rest/api-reference/wix-forms/form-submissions/introduction) associated with the plan at checkout.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          submissionId: z
            .string()
            .describe(
              "ID of a submission to the plan's order form at checkout. Every time a visitor completes the checkout process for a plan, a new submission is created."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          submissionData: z
            .record(z.string(), z.any())
            .describe("Data submitted to the plan's order form at checkout.")
            .optional(),
        })
        .describe(
          "Information about the form submitted during the plan's checkout."
        )
        .optional(),
    })
    .describe('The previewed order, as if the plan had been ordered.')
    .optional(),
  purchaseLimitExceeded: z
    .boolean()
    .describe(
      'Whether this previewed order would exceed the permitted amount of purchases available\nfor this plan for this buyer.\n\nAlways `false` for plans that do not have purchase limits.'
    )
    .optional(),
});
export const GetPricePreviewRequest = z.object({
  planId: z
    .string()
    .describe('ID of plan to preview.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      couponCode: z
        .string()
        .describe('Coupon code to apply, from the Coupons API.')
        .min(1)
        .max(20)
        .optional()
        .nullable(),
    })
    .describe('Options for getting a price preview. ')
    .optional(),
});
export const GetPricePreviewResponse = z.object({
  price: z
    .intersection(
      z.object({
        subtotal: z
          .string()
          .describe(
            'Price of the order excluding tax, specified as a monetary amount. for example, `"9.99"`.'
          )
          .optional(),
        discount: z.string().describe('Total discount applied.').optional(),
        tax: z
          .object({
            name: z
              .string()
              .describe('Name of the tax. For example, VAT.')
              .optional(),
            includedInPrice: z
              .boolean()
              .describe(
                'Whether tax is included in the original price. When `false`, tax is added at checkout.'
              )
              .optional(),
            rate: z
              .string()
              .describe(
                'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
              )
              .optional(),
            amount: z
              .string()
              .describe(
                'Total tax, specified as a monetary amount. For example, `"3.99"`.'
              )
              .optional(),
          })
          .describe('Tax applied.')
          .optional(),
        total: z
          .string()
          .describe(
            'Price after tax and discount is applied, specified as a monetary amount. For example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
          )
          .optional(),
        planPrice: z
          .string()
          .describe('Plan price as it was at the moment of order creation.')
          .optional(),
        currency: z
          .string()
          .describe(
            'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
          )
          .optional(),
        freeTrialDays: z
          .number()
          .int()
          .describe(
            'Free trial period for the order in days. Only available for recurring plans.'
          )
          .min(1)
          .max(999)
          .optional()
          .nullable(),
        coupon: z
          .object({
            code: z
              .string()
              .describe('Code of the applied coupon.')
              .min(1)
              .max(20)
              .optional(),
            amount: z
              .string()
              .describe('Total discount of the coupon, as a monetary amount.')
              .optional(),
            _id: z
              .string()
              .describe('Coupon ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe(
            'Coupon applied to the order. Empty means no coupon was applied.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          subscription: z.never().optional(),
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
        }),
        z.object({
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
          subscription: z
            .object({
              cycleDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                )
                .optional(),
              cycleCount: z
                .number()
                .int()
                .describe(
                  "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                )
                .min(0)
                .max(2000)
                .optional()
                .nullable(),
            })
            .describe('Order has recurring payments.'),
        }),
        z.object({
          subscription: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
          singlePaymentForDuration: z
            .object({
              count: z
                .number()
                .int()
                .describe(
                  'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                )
                .min(1)
                .optional()
                .nullable(),
              unit: z
                .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                .describe('Unit of time for the cycle duration.')
                .optional(),
            })
            .describe(
              'One-time payment. Order is valid for a specified duration.'
            ),
        }),
        z.object({
          subscription: z.never().optional(),
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z
            .boolean()
            .describe('One-time payment. Order is valid until it is canceled.'),
        }),
      ])
    )
    .describe(
      '__Deprecated.__ Use `prices` instead. This property will be removed on September 30, 2022.'
    )
    .optional(),
  prices: z
    .array(
      z.object({
        duration: z
          .object({
            cycleFrom: z
              .number()
              .int()
              .describe(
                'Price starts to apply with this cycle.\n\n`1` is the first payment cycle for all pricing models.'
              )
              .optional(),
            numberOfCycles: z
              .number()
              .int()
              .describe(
                'Amount of cycles to apply price for.\n\nFor `subscription` pricing models with a finite number of cycles, the `numberOfCycles` is the same as `pricing.subscription.cycleCount`.\n\nFor `subscription` pricing models that are unlimited or until-canceled, the `numberOfCycles` is not returned.\n\nFor `singlePaymentForDuration` and `singlePaymentUnlimited` pricing models, the `numberOfCycles` is `1`.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Cycle duration to apply `price` for.\n\nUse with all pricing models.\nCan apply the same price to multiple payment cycles.'
          )
          .optional(),
        price: z
          .object({
            subtotal: z
              .string()
              .describe(
                'Price of the order excluding tax, specified as a monetary amount. For example, `"9.99"`.'
              )
              .optional(),
            coupon: z
              .object({
                code: z
                  .string()
                  .describe('Code of the applied coupon.')
                  .min(1)
                  .max(20)
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Total discount of the coupon, as a monetary amount.'
                  )
                  .optional(),
                _id: z
                  .string()
                  .describe('Coupon ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe('Coupon applied to the order, from the Coupons API.')
              .optional(),
            discount: z
              .string()
              .describe('Total discount applied to the order.')
              .optional(),
            tax: z
              .object({
                name: z
                  .string()
                  .describe('Name of the tax. For example, VAT.')
                  .optional(),
                includedInPrice: z
                  .boolean()
                  .describe(
                    'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                  )
                  .optional(),
                rate: z
                  .string()
                  .describe(
                    'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                  )
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                  )
                  .optional(),
              })
              .describe(
                'Tax applied to the order.\n\nTax is only applied if the site [has it configured](https://support.wix.com/en/article/pricing-plans-setting-up-tax-collection).'
              )
              .optional(),
            total: z
              .string()
              .describe(
                'Price after tax and discount is applied. Specified as a monetary amount, for example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Three-letter currency code in\n[ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
              )
              .optional(),
            proration: z
              .string()
              .describe(
                'Price change after billing date was change and price was adjusted. Could be positive and negative values.'
              )
              .optional(),
          })
          .describe('Order price.')
          .optional(),
      })
    )
    .optional(),
});
export const ManagementGetOrderRequest = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({ fieldSet: z.enum(['UNKNOWN_SET', 'BASIC', 'FULL']).optional() })
    .describe('Options to use when getting an order. ')
    .optional(),
});
export const ManagementGetOrderResponse = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  planId: z
    .string()
    .describe('ID of the plan purchased with the order, from the Plans API.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  subscriptionId: z
    .string()
    .describe(
      'ID of the related Wix subscription.\n\nEvery pricing plan order corresponds to a Wix subscription, including orders for single payment plans. Learn more in\na [Pricing Plans overview](https://support.wix.com/en/article/pricing-plans-an-overview#create-plans-to-suit-your-business).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  wixPayOrderId: z
    .string()
    .describe(
      'Wix Pay order ID.\n\nProvided by Wix whether the order is created online or offline. The field is omitted when the order is free.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  buyer: z
    .object({
      memberId: z
        .string()
        .describe('Member ID for a Wix site member, from the Members API.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID for a Wix site contact, from the Contacts API.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
    })
    .describe(
      "The buyer's IDs. Includes `memberId` and `contactId`.\n\nCurrently, Pricing Plan purchases are limited to members only. `contactId` is returned,\nbut a buyer will not be able to purchase a plan without a `memberId`."
    )
    .optional(),
  priceDetails: z
    .intersection(
      z.object({
        subtotal: z
          .string()
          .describe(
            'Price of the order excluding tax, specified as a monetary amount. for example, `"9.99"`.'
          )
          .optional(),
        discount: z.string().describe('Total discount applied.').optional(),
        tax: z
          .object({
            name: z
              .string()
              .describe('Name of the tax. For example, VAT.')
              .optional(),
            includedInPrice: z
              .boolean()
              .describe(
                'Whether tax is included in the original price. When `false`, tax is added at checkout.'
              )
              .optional(),
            rate: z
              .string()
              .describe(
                'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
              )
              .optional(),
            amount: z
              .string()
              .describe(
                'Total tax, specified as a monetary amount. For example, `"3.99"`.'
              )
              .optional(),
          })
          .describe('Tax applied.')
          .optional(),
        total: z
          .string()
          .describe(
            'Price after tax and discount is applied, specified as a monetary amount. For example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
          )
          .optional(),
        planPrice: z
          .string()
          .describe('Plan price as it was at the moment of order creation.')
          .optional(),
        currency: z
          .string()
          .describe(
            'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
          )
          .optional(),
        freeTrialDays: z
          .number()
          .int()
          .describe(
            'Free trial period for the order in days. Only available for recurring plans.'
          )
          .min(1)
          .max(999)
          .optional()
          .nullable(),
        coupon: z
          .object({
            code: z
              .string()
              .describe('Code of the applied coupon.')
              .min(1)
              .max(20)
              .optional(),
            amount: z
              .string()
              .describe('Total discount of the coupon, as a monetary amount.')
              .optional(),
            _id: z
              .string()
              .describe('Coupon ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe(
            'Coupon applied to the order. Empty means no coupon was applied.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          subscription: z.never().optional(),
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
        }),
        z.object({
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
          subscription: z
            .object({
              cycleDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                )
                .optional(),
              cycleCount: z
                .number()
                .int()
                .describe(
                  "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                )
                .min(0)
                .max(2000)
                .optional()
                .nullable(),
            })
            .describe('Order has recurring payments.'),
        }),
        z.object({
          subscription: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
          singlePaymentForDuration: z
            .object({
              count: z
                .number()
                .int()
                .describe(
                  'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                )
                .min(1)
                .optional()
                .nullable(),
              unit: z
                .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                .describe('Unit of time for the cycle duration.')
                .optional(),
            })
            .describe(
              'One-time payment. Order is valid for a specified duration.'
            ),
        }),
        z.object({
          subscription: z.never().optional(),
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z
            .boolean()
            .describe('One-time payment. Order is valid until it is canceled.'),
        }),
      ])
    )
    .describe(
      '__Deprecated.__ Use `pricing` instead. This property will be removed on September 30, 2022.'
    )
    .optional(),
  pricing: z
    .intersection(
      z.object({
        prices: z
          .array(
            z.object({
              duration: z
                .object({
                  cycleFrom: z
                    .number()
                    .int()
                    .describe(
                      'Price starts to apply with this cycle.\n\n`1` is the first payment cycle for all pricing models.'
                    )
                    .optional(),
                  numberOfCycles: z
                    .number()
                    .int()
                    .describe(
                      'Amount of cycles to apply price for.\n\nFor `subscription` pricing models with a finite number of cycles, the `numberOfCycles` is the same as `pricing.subscription.cycleCount`.\n\nFor `subscription` pricing models that are unlimited or until-canceled, the `numberOfCycles` is not returned.\n\nFor `singlePaymentForDuration` and `singlePaymentUnlimited` pricing models, the `numberOfCycles` is `1`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Cycle duration to apply `price` for.\n\nUse with all pricing models.\nCan apply the same price to multiple payment cycles.'
                )
                .optional(),
              price: z
                .object({
                  subtotal: z
                    .string()
                    .describe(
                      'Price of the order excluding tax, specified as a monetary amount. For example, `"9.99"`.'
                    )
                    .optional(),
                  coupon: z
                    .object({
                      code: z
                        .string()
                        .describe('Code of the applied coupon.')
                        .min(1)
                        .max(20)
                        .optional(),
                      amount: z
                        .string()
                        .describe(
                          'Total discount of the coupon, as a monetary amount.'
                        )
                        .optional(),
                      _id: z
                        .string()
                        .describe('Coupon ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    })
                    .describe(
                      'Coupon applied to the order, from the Coupons API.'
                    )
                    .optional(),
                  discount: z
                    .string()
                    .describe('Total discount applied to the order.')
                    .optional(),
                  tax: z
                    .object({
                      name: z
                        .string()
                        .describe('Name of the tax. For example, VAT.')
                        .optional(),
                      includedInPrice: z
                        .boolean()
                        .describe(
                          'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                        )
                        .optional(),
                      rate: z
                        .string()
                        .describe(
                          'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                        )
                        .optional(),
                      amount: z
                        .string()
                        .describe(
                          'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Tax applied to the order.\n\nTax is only applied if the site [has it configured](https://support.wix.com/en/article/pricing-plans-setting-up-tax-collection).'
                    )
                    .optional(),
                  total: z
                    .string()
                    .describe(
                      'Price after tax and discount is applied. Specified as a monetary amount, for example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Three-letter currency code in\n[ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                    )
                    .optional(),
                  proration: z
                    .string()
                    .describe(
                      'Price change after billing date was change and price was adjusted. Could be positive and negative values.'
                    )
                    .optional(),
                })
                .describe('Order price.')
                .optional(),
            })
          )
          .optional(),
      }),
      z.xor([
        z.object({
          subscription: z.never().optional(),
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
        }),
        z.object({
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
          subscription: z
            .object({
              cycleDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                )
                .optional(),
              cycleCount: z
                .number()
                .int()
                .describe(
                  "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                )
                .min(0)
                .max(2000)
                .optional()
                .nullable(),
            })
            .describe(
              'Pricing model for an order with recurring payment cycles.'
            ),
        }),
        z.object({
          subscription: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
          singlePaymentForDuration: z
            .object({
              count: z
                .number()
                .int()
                .describe(
                  'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                )
                .min(1)
                .optional()
                .nullable(),
              unit: z
                .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                .describe('Unit of time for the cycle duration.')
                .optional(),
            })
            .describe(
              'Pricing model for an order with a one-time payment and the order is valid for a specific amount of time.'
            ),
        }),
        z.object({
          subscription: z.never().optional(),
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z
            .boolean()
            .describe(
              'Pricing model for an order with a one-time payment and the order is valid until canceled.'
            ),
        }),
      ])
    )
    .describe(
      'Order pricing model, price, and payment schedule.\n\nLearn more about [pricing models](https://dev.wix.com/api/rest/wix-pricing-plans/pricing-plans/introduction#wix-pricing-plans_pricing-plans_introduction_pricing-models).'
    )
    .optional(),
  type: z
    .enum(['UNDEFINED', 'ONLINE', 'OFFLINE', 'EXTERNAL'])
    .describe('How the order was processed.')
    .optional(),
  status: z
    .enum([
      'UNDEFINED',
      'DRAFT',
      'PENDING',
      'ACTIVE',
      'PAUSED',
      'ENDED',
      'CANCELED',
    ])
    .describe('Status of the order.')
    .optional(),
  autoRenewCanceled: z
    .boolean()
    .describe(
      "Whether the order will be canceled at the next payment date.\n\nIf `true`, the order status will be `CANCELED` and the next payment won't be charged. Omitted for single payment orders."
    )
    .optional()
    .nullable(),
  cancellation: z
    .object({
      requestedDate: z
        .date()
        .describe('Date and time the cancellation was requested.')
        .optional()
        .nullable(),
      cause: z
        .enum([
          'UNDEFINED',
          'OWNER_ACTION',
          'MEMBER_ACTION',
          'PAYMENT_FAILURE',
          'PAYMENT_SETUP_FAILURE',
          'UNKNOWN',
        ])
        .describe('Reason for the cancellation.')
        .optional(),
      effectiveAt: z
        .enum(['UNDEFINED', 'IMMEDIATELY', 'NEXT_PAYMENT_DATE'])
        .describe(
          'When the cancellation takes effect. Set when cancelling the order.'
        )
        .optional(),
    })
    .describe(
      'Details about the cancellation of an order.\n\nOnly present if the status is `CANCELED`.'
    )
    .optional(),
  lastPaymentStatus: z
    .enum([
      'UNDEFINED',
      'PAID',
      'REFUNDED',
      'FAILED',
      'UNPAID',
      'PENDING',
      'NOT_APPLICABLE',
    ])
    .describe(
      'Status of the last payment for the order.\nUpdated automatically for online orders. Updated manually by the Wix user for offline orders.'
    )
    .optional(),
  startDate: z
    .date()
    .describe('Start date and time for the ordered plan.')
    .optional()
    .nullable(),
  endDate: z
    .date()
    .describe(
      'Current end date and time for the ordered plan.\n\n`endDate` may be updated over the course of an order.\nIf the order is paused, it will have a later `endDate` once it is resumed.\n`endDate` may also be postponed.\n\nOmitted if the order is valid until canceled and still `ACTIVE`.'
    )
    .optional()
    .nullable(),
  pausePeriods: z
    .array(
      z.object({
        status: z
          .enum(['UNDEFINED', 'ACTIVE', 'ENDED'])
          .describe('Status of the pause period.')
          .optional(),
        pauseDate: z
          .date()
          .describe('Start date and time of the pause period.')
          .optional()
          .nullable(),
        resumeDate: z
          .date()
          .describe(
            'End date and time of the pause period.\n\nOmitted while the pause period remains `ACTIVE`.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  freeTrialDays: z
    .number()
    .int()
    .describe(
      'Free trial period for the order, in days.\n\nOnly available for recurring plans.'
    )
    .min(1)
    .max(999)
    .optional()
    .nullable(),
  earliestEndDate: z
    .date()
    .describe(
      'Earliest end date and time that the plan for the order can expire.\n\nCalculated by using the original end date plus any pause periods. Omitted if the order is active until canceled. Reserved for future use.'
    )
    .optional()
    .nullable(),
  currentCycle: z
    .object({
      index: z
        .number()
        .int()
        .describe(
          'Index of the current payment cycle in the order.\n\n`0` when order is in a free trial period. In all other cases, the index starts with `1`.'
        )
        .min(0)
        .optional(),
      startedDate: z
        .date()
        .describe('Start date and time for the current payment cycle.')
        .optional()
        .nullable(),
      endedDate: z
        .date()
        .describe('End date and time for the current payment cycle.')
        .optional()
        .nullable(),
    })
    .describe(
      "Current payment cycle for the order.\n\n`currentCycle` will be omitted if the order's status is `CANCELED` or `ENDED`, or if the `startDate` hasn't passed yet."
    )
    .optional(),
  planName: z
    .string()
    .describe('Plan name at the time of purchase.')
    .optional(),
  planDescription: z
    .string()
    .describe('Plan description at the time of purchase')
    .optional(),
  planPrice: z
    .string()
    .describe('Plan price as it was at the moment of order creation.')
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time the order was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the order was updated.')
    .optional()
    .nullable(),
  formData: z
    .object({
      formId: z
        .string()
        .describe(
          'ID of the [order form](https://dev.wix.com/docs/rest/api-reference/wix-forms/form-submissions/introduction) associated with the plan at checkout.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      submissionId: z
        .string()
        .describe(
          "ID of a submission to the plan's order form at checkout. Every time a visitor completes the checkout process for a plan, a new submission is created."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      submissionData: z
        .record(z.string(), z.any())
        .describe("Data submitted to the plan's order form at checkout.")
        .optional(),
    })
    .describe(
      "Information about the form submitted during the plan's checkout."
    )
    .optional(),
});
export const ManagementListOrdersRequest = z.object({
  options: z
    .object({
      buyerIds: z.array(z.string()).optional(),
      planIds: z.array(z.string()).optional(),
      autoRenewCanceled: z
        .boolean()
        .describe(
          'Filter by whether or not the auto-renewal of recurring orders was canceled.'
        )
        .optional()
        .nullable(),
      orderStatuses: z
        .array(
          z.enum([
            'UNDEFINED',
            'DRAFT',
            'PENDING',
            'ACTIVE',
            'PAUSED',
            'ENDED',
            'CANCELED',
          ])
        )
        .optional(),
      paymentStatuses: z
        .array(
          z.enum([
            'UNDEFINED',
            'PAID',
            'REFUNDED',
            'FAILED',
            'UNPAID',
            'PENDING',
            'NOT_APPLICABLE',
          ])
        )
        .optional(),
      limit: z
        .number()
        .int()
        .describe(
          'Number of orders to return. See Sorting and Paging for more information.\n\nMax: `50`'
        )
        .min(1)
        .max(50)
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Number of orders to skip in the current sort order.')
        .min(0)
        .optional()
        .nullable(),
      sorting: z
        .object({
          fieldName: z
            .string()
            .describe('Name of the field to sort by.')
            .max(512)
            .optional(),
          order: z.enum(['ASC', 'DESC']).optional(),
        })
        .describe(
          'Sort order.\n\nUse `ASC` for ascending order or `DESC` for descending order.\n\nDefault: `DESC`.'
        )
        .optional(),
      fieldSet: z.enum(['UNKNOWN_SET', 'BASIC', 'FULL']).optional(),
    })
    .describe('Filtering, sorting, and pagination options. ')
    .optional(),
});
export const ManagementListOrdersResponse = z.object({
  orders: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Order ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        planId: z
          .string()
          .describe(
            'ID of the plan purchased with the order, from the Plans API.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        subscriptionId: z
          .string()
          .describe(
            'ID of the related Wix subscription.\n\nEvery pricing plan order corresponds to a Wix subscription, including orders for single payment plans. Learn more in\na [Pricing Plans overview](https://support.wix.com/en/article/pricing-plans-an-overview#create-plans-to-suit-your-business).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        wixPayOrderId: z
          .string()
          .describe(
            'Wix Pay order ID.\n\nProvided by Wix whether the order is created online or offline. The field is omitted when the order is free.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        buyer: z
          .object({
            memberId: z
              .string()
              .describe(
                'Member ID for a Wix site member, from the Members API.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            contactId: z
              .string()
              .describe(
                'Contact ID for a Wix site contact, from the Contacts API.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe(
            "The buyer's IDs. Includes `memberId` and `contactId`.\n\nCurrently, Pricing Plan purchases are limited to members only. `contactId` is returned,\nbut a buyer will not be able to purchase a plan without a `memberId`."
          )
          .optional(),
        priceDetails: z
          .intersection(
            z.object({
              subtotal: z
                .string()
                .describe(
                  'Price of the order excluding tax, specified as a monetary amount. for example, `"9.99"`.'
                )
                .optional(),
              discount: z
                .string()
                .describe('Total discount applied.')
                .optional(),
              tax: z
                .object({
                  name: z
                    .string()
                    .describe('Name of the tax. For example, VAT.')
                    .optional(),
                  includedInPrice: z
                    .boolean()
                    .describe(
                      'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                    )
                    .optional(),
                  rate: z
                    .string()
                    .describe(
                      'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                    )
                    .optional(),
                  amount: z
                    .string()
                    .describe(
                      'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                    )
                    .optional(),
                })
                .describe('Tax applied.')
                .optional(),
              total: z
                .string()
                .describe(
                  'Price after tax and discount is applied, specified as a monetary amount. For example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
                )
                .optional(),
              planPrice: z
                .string()
                .describe(
                  'Plan price as it was at the moment of order creation.'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code. Must be valid ISO 4217 currency code (e.g., USD).'
                )
                .optional(),
              freeTrialDays: z
                .number()
                .int()
                .describe(
                  'Free trial period for the order in days. Only available for recurring plans.'
                )
                .min(1)
                .max(999)
                .optional()
                .nullable(),
              coupon: z
                .object({
                  code: z
                    .string()
                    .describe('Code of the applied coupon.')
                    .min(1)
                    .max(20)
                    .optional(),
                  amount: z
                    .string()
                    .describe(
                      'Total discount of the coupon, as a monetary amount.'
                    )
                    .optional(),
                  _id: z
                    .string()
                    .describe('Coupon ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                })
                .describe(
                  'Coupon applied to the order. Empty means no coupon was applied.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
              }),
              z.object({
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                subscription: z
                  .object({
                    cycleDuration: z
                      .object({
                        count: z
                          .number()
                          .int()
                          .describe(
                            'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                        unit: z
                          .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                          .describe('Unit of time for the cycle duration.')
                          .optional(),
                      })
                      .describe(
                        'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                      )
                      .optional(),
                    cycleCount: z
                      .number()
                      .int()
                      .describe(
                        "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                      )
                      .min(0)
                      .max(2000)
                      .optional()
                      .nullable(),
                  })
                  .describe('Order has recurring payments.'),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                singlePaymentForDuration: z
                  .object({
                    count: z
                      .number()
                      .int()
                      .describe(
                        'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    unit: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe('Unit of time for the cycle duration.')
                      .optional(),
                  })
                  .describe(
                    'One-time payment. Order is valid for a specified duration.'
                  ),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z
                  .boolean()
                  .describe(
                    'One-time payment. Order is valid until it is canceled.'
                  ),
              }),
            ])
          )
          .describe(
            '__Deprecated.__ Use `pricing` instead. This property will be removed on September 30, 2022.'
          )
          .optional(),
        pricing: z
          .intersection(
            z.object({
              prices: z
                .array(
                  z.object({
                    duration: z
                      .object({
                        cycleFrom: z
                          .number()
                          .int()
                          .describe(
                            'Price starts to apply with this cycle.\n\n`1` is the first payment cycle for all pricing models.'
                          )
                          .optional(),
                        numberOfCycles: z
                          .number()
                          .int()
                          .describe(
                            'Amount of cycles to apply price for.\n\nFor `subscription` pricing models with a finite number of cycles, the `numberOfCycles` is the same as `pricing.subscription.cycleCount`.\n\nFor `subscription` pricing models that are unlimited or until-canceled, the `numberOfCycles` is not returned.\n\nFor `singlePaymentForDuration` and `singlePaymentUnlimited` pricing models, the `numberOfCycles` is `1`.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Cycle duration to apply `price` for.\n\nUse with all pricing models.\nCan apply the same price to multiple payment cycles.'
                      )
                      .optional(),
                    price: z
                      .object({
                        subtotal: z
                          .string()
                          .describe(
                            'Price of the order excluding tax, specified as a monetary amount. For example, `"9.99"`.'
                          )
                          .optional(),
                        coupon: z
                          .object({
                            code: z
                              .string()
                              .describe('Code of the applied coupon.')
                              .min(1)
                              .max(20)
                              .optional(),
                            amount: z
                              .string()
                              .describe(
                                'Total discount of the coupon, as a monetary amount.'
                              )
                              .optional(),
                            _id: z
                              .string()
                              .describe('Coupon ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          })
                          .describe(
                            'Coupon applied to the order, from the Coupons API.'
                          )
                          .optional(),
                        discount: z
                          .string()
                          .describe('Total discount applied to the order.')
                          .optional(),
                        tax: z
                          .object({
                            name: z
                              .string()
                              .describe('Name of the tax. For example, VAT.')
                              .optional(),
                            includedInPrice: z
                              .boolean()
                              .describe(
                                'Whether tax is included in the original price. When `false`, tax is added at checkout.'
                              )
                              .optional(),
                            rate: z
                              .string()
                              .describe(
                                'Tax rate percentage, as a number between 0 and 100. For example, a 7% tax rate is `"7.00"`.'
                              )
                              .optional(),
                            amount: z
                              .string()
                              .describe(
                                'Total tax, specified as a monetary amount. For example, `"3.99"`.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Tax applied to the order.\n\nTax is only applied if the site [has it configured](https://support.wix.com/en/article/pricing-plans-setting-up-tax-collection).'
                          )
                          .optional(),
                        total: z
                          .string()
                          .describe(
                            'Price after tax and discount is applied. Specified as a monetary amount, for example, `"13.98"`.\n\nIf no tax is applied, the amount is the same as `subtotal`.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Three-letter currency code in\n[ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                          )
                          .optional(),
                        proration: z
                          .string()
                          .describe(
                            'Price change after billing date was change and price was adjusted. Could be positive and negative values.'
                          )
                          .optional(),
                      })
                      .describe('Order price.')
                      .optional(),
                  })
                )
                .optional(),
            }),
            z.xor([
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
              }),
              z.object({
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                subscription: z
                  .object({
                    cycleDuration: z
                      .object({
                        count: z
                          .number()
                          .int()
                          .describe(
                            'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                        unit: z
                          .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                          .describe('Unit of time for the cycle duration.')
                          .optional(),
                      })
                      .describe(
                        'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                      )
                      .optional(),
                    cycleCount: z
                      .number()
                      .int()
                      .describe(
                        "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                      )
                      .min(0)
                      .max(2000)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Pricing model for an order with recurring payment cycles.'
                  ),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                singlePaymentForDuration: z
                  .object({
                    count: z
                      .number()
                      .int()
                      .describe(
                        'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    unit: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe('Unit of time for the cycle duration.')
                      .optional(),
                  })
                  .describe(
                    'Pricing model for an order with a one-time payment and the order is valid for a specific amount of time.'
                  ),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z
                  .boolean()
                  .describe(
                    'Pricing model for an order with a one-time payment and the order is valid until canceled.'
                  ),
              }),
            ])
          )
          .describe(
            'Order pricing model, price, and payment schedule.\n\nLearn more about [pricing models](https://dev.wix.com/api/rest/wix-pricing-plans/pricing-plans/introduction#wix-pricing-plans_pricing-plans_introduction_pricing-models).'
          )
          .optional(),
        type: z
          .enum(['UNDEFINED', 'ONLINE', 'OFFLINE', 'EXTERNAL'])
          .describe('How the order was processed.')
          .optional(),
        status: z
          .enum([
            'UNDEFINED',
            'DRAFT',
            'PENDING',
            'ACTIVE',
            'PAUSED',
            'ENDED',
            'CANCELED',
          ])
          .describe('Status of the order.')
          .optional(),
        autoRenewCanceled: z
          .boolean()
          .describe(
            "Whether the order will be canceled at the next payment date.\n\nIf `true`, the order status will be `CANCELED` and the next payment won't be charged. Omitted for single payment orders."
          )
          .optional()
          .nullable(),
        cancellation: z
          .object({
            requestedDate: z
              .date()
              .describe('Date and time the cancellation was requested.')
              .optional()
              .nullable(),
            cause: z
              .enum([
                'UNDEFINED',
                'OWNER_ACTION',
                'MEMBER_ACTION',
                'PAYMENT_FAILURE',
                'PAYMENT_SETUP_FAILURE',
                'UNKNOWN',
              ])
              .describe('Reason for the cancellation.')
              .optional(),
            effectiveAt: z
              .enum(['UNDEFINED', 'IMMEDIATELY', 'NEXT_PAYMENT_DATE'])
              .describe(
                'When the cancellation takes effect. Set when cancelling the order.'
              )
              .optional(),
          })
          .describe(
            'Details about the cancellation of an order.\n\nOnly present if the status is `CANCELED`.'
          )
          .optional(),
        lastPaymentStatus: z
          .enum([
            'UNDEFINED',
            'PAID',
            'REFUNDED',
            'FAILED',
            'UNPAID',
            'PENDING',
            'NOT_APPLICABLE',
          ])
          .describe(
            'Status of the last payment for the order.\nUpdated automatically for online orders. Updated manually by the Wix user for offline orders.'
          )
          .optional(),
        startDate: z
          .date()
          .describe('Start date and time for the ordered plan.')
          .optional()
          .nullable(),
        endDate: z
          .date()
          .describe(
            'Current end date and time for the ordered plan.\n\n`endDate` may be updated over the course of an order.\nIf the order is paused, it will have a later `endDate` once it is resumed.\n`endDate` may also be postponed.\n\nOmitted if the order is valid until canceled and still `ACTIVE`.'
          )
          .optional()
          .nullable(),
        pausePeriods: z
          .array(
            z.object({
              status: z
                .enum(['UNDEFINED', 'ACTIVE', 'ENDED'])
                .describe('Status of the pause period.')
                .optional(),
              pauseDate: z
                .date()
                .describe('Start date and time of the pause period.')
                .optional()
                .nullable(),
              resumeDate: z
                .date()
                .describe(
                  'End date and time of the pause period.\n\nOmitted while the pause period remains `ACTIVE`.'
                )
                .optional()
                .nullable(),
            })
          )
          .optional(),
        freeTrialDays: z
          .number()
          .int()
          .describe(
            'Free trial period for the order, in days.\n\nOnly available for recurring plans.'
          )
          .min(1)
          .max(999)
          .optional()
          .nullable(),
        earliestEndDate: z
          .date()
          .describe(
            'Earliest end date and time that the plan for the order can expire.\n\nCalculated by using the original end date plus any pause periods. Omitted if the order is active until canceled. Reserved for future use.'
          )
          .optional()
          .nullable(),
        currentCycle: z
          .object({
            index: z
              .number()
              .int()
              .describe(
                'Index of the current payment cycle in the order.\n\n`0` when order is in a free trial period. In all other cases, the index starts with `1`.'
              )
              .min(0)
              .optional(),
            startedDate: z
              .date()
              .describe('Start date and time for the current payment cycle.')
              .optional()
              .nullable(),
            endedDate: z
              .date()
              .describe('End date and time for the current payment cycle.')
              .optional()
              .nullable(),
          })
          .describe(
            "Current payment cycle for the order.\n\n`currentCycle` will be omitted if the order's status is `CANCELED` or `ENDED`, or if the `startDate` hasn't passed yet."
          )
          .optional(),
        planName: z
          .string()
          .describe('Plan name at the time of purchase.')
          .optional(),
        planDescription: z
          .string()
          .describe('Plan description at the time of purchase')
          .optional(),
        planPrice: z
          .string()
          .describe('Plan price as it was at the moment of order creation.')
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the order was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the order was updated.')
          .optional()
          .nullable(),
        formData: z
          .object({
            formId: z
              .string()
              .describe(
                'ID of the [order form](https://dev.wix.com/docs/rest/api-reference/wix-forms/form-submissions/introduction) associated with the plan at checkout.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            submissionId: z
              .string()
              .describe(
                "ID of a submission to the plan's order form at checkout. Every time a visitor completes the checkout process for a plan, a new submission is created."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            submissionData: z
              .record(z.string(), z.any())
              .describe("Data submitted to the plan's order form at checkout.")
              .optional(),
          })
          .describe(
            "Information about the form submitted during the plan's checkout."
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe(
      'Object containing paging-related data (number of orders returned, offset).'
    )
    .optional(),
});
export const PostponeEndDateRequest = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  endDate: z
    .date()
    .describe(
      'New end date and time.\n\nMust be later than the current end date and time.'
    ),
});
export const PostponeEndDateResponse = z.object({});
export const CancelOrderRequest = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  effectiveAt: z.enum(['UNDEFINED', 'IMMEDIATELY', 'NEXT_PAYMENT_DATE']),
});
export const CancelOrderResponse = z.object({});
export const MarkAsPaidRequest = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const MarkAsPaidResponse = z.object({});
export const PauseOrderRequest = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const PauseOrderResponse = z.object({});
export const ResumeOrderRequest = z.object({
  _id: z
    .string()
    .describe('Order ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const ResumeOrderResponse = z.object({});
